import { Card } from "@pericles/framework-frontend";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  createFoundryDecisionDiscussion,
  createFoundryV2CompositionDraft,
  createFoundryV2ContractV1Draft,
  createFoundryV2Handoff,
  importLegacyRepoFoundryExternalIntake,
  fetchLatestFoundryV2ContractV1Draft,
  fetchLatestFoundryV2CompositionDraft,
  fetchLatestFoundryV2Handoff,
  fetchFoundryDecisionLedger,
  fetchFoundryDecisionEngine,
  fetchFoundrySpecValidator,
  fetchFoundryV2ArchitectureRecommendation,
  fetchFoundryV2DomainPacks,
  fetchFoundryV2EclosiaBenchmark,
  fetchFoundryV2ExecutionPlanPreview,
  fetchFoundryV2JarvisEvaluation,
  fetchFoundryV2ContextGate,
  fetchFoundryV2DeterministicAssembly,
  fetchFoundryV2LecV2,
  fetchFoundryV2OrganizationMemory,
  fetchFoundryV2QaFactory,
  fetchFoundryV2RuntimeConnectors,
  fetchFoundryV2RuntimeRelease,
  fetchFoundryV2SimulationEngine,
  fetchFoundryV2Traceability,
  promoteFoundryDecision,
  refreshFoundryDecisionEngine,
  type FoundryDecisionLedgerRecord,
  type FoundryDecisionEngineRecord,
  type FoundryLegacyExternalIntakeResponse,
  type FoundrySpecValidatorRecord,
  type FoundryV2CompositionDraftRecord,
  FoundryV2ContractV1Error,
  FoundryV2HandoffError,
  type FoundryContractV1DraftRecord,
  type FoundryV2HandoffRecord,
  type FoundryV2ArchitectureRecommendation,
  type FoundryV2ContextGateResponse,
  type FoundryV2DomainPacksRecord,
  type FoundryV2DeterministicAssemblyRecord,
  type FoundryV2EclosiaBenchmarkRecord,
  type FoundryV2ExecutionPlanPreview,
  type FoundryV2JarvisEvaluationRecord,
  type FoundryV2LecV2Record,
  type FoundryV2OrganizationMemoryRecord,
  type FoundryV2QaFactoryRecord,
  type FoundryV2RuntimeConnectorsRecord,
  type FoundryV2RuntimeReleaseRecord,
  type FoundryV2SimulationEngineRecord,
  type FoundryV2TraceabilityRecord,
} from "../api/foundryV2Client";
import {
  fetchLongwallsLegacyReverseEngineeringPack,
  fetchLongwallsPilotGate,
  type LongwallsEnterprisePilotGate,
  type LongwallsLegacyReverseEngineeringPack,
} from "../api/longwallsEnterpriseClient";
import { resolveProjectReference } from "../api/projectsClient";

import { FoundryV2AccessGuard } from "./FoundryV2AccessGuard";
import { FoundryAssemblyContractPanel } from "./FoundryAssemblyContractPanel";
import { FoundryBrickSelectionPanel } from "./FoundryBrickSelectionPanel";
import { FoundryDecisionChecklistPanel } from "./FoundryDecisionChecklistPanel";
import { FoundryDecisionLedgerPanel } from "./FoundryDecisionLedgerPanel";
import { FoundryDeterministicAssemblyPanel } from "./FoundryDeterministicAssemblyPanel";
import { FoundryDomainPacksPanel } from "./FoundryDomainPacksPanel";
import { FoundryDomainProfilePanel } from "./FoundryDomainProfilePanel";
import { FoundryEclosiaBenchmarkPanel } from "./FoundryEclosiaBenchmarkPanel";
import { FoundryJarvisEvaluationPanel } from "./FoundryJarvisEvaluationPanel";
import { FoundryOrganizationMemoryPanel } from "./FoundryOrganizationMemoryPanel";
import { FoundryQaFactoryPanel } from "./FoundryQaFactoryPanel";
import { FoundryRuntimeConnectorsPanel } from "./FoundryRuntimeConnectorsPanel";
import { FoundryRuntimeReleasePanel } from "./FoundryRuntimeReleasePanel";
import { FoundrySimulationEnginePanel } from "./FoundrySimulationEnginePanel";
import { FoundrySpecValidatorPanel } from "./FoundrySpecValidatorPanel";
import { FoundryTraceabilityPanel } from "./FoundryTraceabilityPanel";
import { ProjectPreDiscoveryPanel } from "./ProjectPreDiscoveryPanel";
import { Button } from "./ui";

type StepId = "context" | "intent" | "architecture" | "environment" | "review";
type IntegrationTarget = "openai" | "supabase" | "erp" | "stripe" | "redis" | "smtp" | "s3" | "google-maps";
type RuntimeProfileId = "local" | "local_by_design" | "customer_server" | "cloud_managed" | "regulated_hybrid";

type FoundryV2Draft = {
  projectId: string;
  appName: string;
  appType: "internal-dashboard" | "customer-portal" | "api-platform" | "data-pipeline";
  businessGoal: string;
  constraints: string;
  runtime: "node" | "python" | "java";
  frontend: "nextjs" | "react-vite" | "nuxt";
  backend: "nestjs" | "express" | "fastapi" | "spring";
  database: "postgres" | "supabase" | "mongodb";
  deployment: "docker" | "vercel" | "cloudflare" | "aws";
  auth: "auth0" | "clerk" | "cognito" | "sso";
  observability: "otel-sentry" | "otel-datadog" | "basic-logs";
  runtimeProfile: RuntimeProfileId;
  githubMode: "local-only" | "existing-repo" | "create-repo";
  repositoryName: string;
  envKeys: string;
  integrationTargets: IntegrationTarget[];
  integrationNotes: string;
  complianceProfile: "standard" | "restricted" | "highly-regulated";
  autoPlanning: boolean;
  transcriptText: string;
  transcriptSource: string;
  transcriptLanguage: string;
  hypotheses: string;
  openQuestions: string;
  mvpScope: string;
  nfr: string;
  risks: string;
  kpis: string;
};

const STORAGE_KEY = "longwalls.v2.foundryDraft";

const STEPS: Array<{ id: StepId; label: string; description: string }> = [
  {
    id: "context",
    label: "Context",
    description: "Capture pre-discovery and discovery evidence.",
  },
  {
    id: "intent",
    label: "Intent",
    description: "Define outcomes and constraints.",
  },
  {
    id: "architecture",
    label: "Architecture",
    description: "Select stack with AI recommendations.",
  },
  {
    id: "environment",
    label: "Environment",
    description: "Prepare runtime and delivery options.",
  },
  {
    id: "review",
    label: "Review",
    description: "Lock the blueprint and hand off to Jarvis.",
  },
];

const DEFAULT_DRAFT: FoundryV2Draft = {
  projectId: "",
  appName: "",
  appType: "internal-dashboard",
  businessGoal: "",
  constraints: "",
  runtime: "node",
  frontend: "nextjs",
  backend: "nestjs",
  database: "postgres",
  deployment: "docker",
  auth: "auth0",
  observability: "otel-sentry",
  runtimeProfile: "local_by_design",
  githubMode: "local-only",
  repositoryName: "",
  envKeys: "",
  integrationTargets: [],
  integrationNotes: "",
  complianceProfile: "standard",
  autoPlanning: true,
  transcriptText: "",
  transcriptSource: "smartphone",
  transcriptLanguage: "fr",
  hypotheses: "",
  openQuestions: "",
  mvpScope: "",
  nfr: "",
  risks: "",
  kpis: "",
};

const LOCAL_AI_RECOMMENDATIONS: Record<FoundryV2Draft["appType"], FoundryV2ArchitectureRecommendation> = {
  "internal-dashboard": {
    appType: "internal-dashboard",
    runtime: "node",
    frontend: "nextjs",
    backend: "nestjs",
    database: "postgres",
    deployment: "docker",
    auth: "sso",
    observability: "otel-datadog",
    confidence: 0.84,
    rationale: "Balanced enterprise setup with strong governance and operational visibility.",
    tradeoffs: { cost: "medium", complexity: "medium", lockIn: "low", compliance: "high" },
  },
  "customer-portal": {
    appType: "customer-portal",
    runtime: "node",
    frontend: "nextjs",
    backend: "nestjs",
    database: "supabase",
    deployment: "vercel",
    auth: "clerk",
    observability: "otel-sentry",
    confidence: 0.82,
    rationale: "Fast product iteration with managed auth and frictionless frontend deployment.",
    tradeoffs: { cost: "low", complexity: "low", lockIn: "high", compliance: "medium" },
  },
  "api-platform": {
    appType: "api-platform",
    runtime: "node",
    frontend: "react-vite",
    backend: "nestjs",
    database: "postgres",
    deployment: "aws",
    auth: "cognito",
    observability: "otel-datadog",
    confidence: 0.87,
    rationale: "API-first architecture optimized for reliability, scale, and control.",
    tradeoffs: { cost: "high", complexity: "high", lockIn: "medium", compliance: "high" },
  },
  "data-pipeline": {
    appType: "data-pipeline",
    runtime: "python",
    frontend: "react-vite",
    backend: "fastapi",
    database: "postgres",
    deployment: "aws",
    auth: "auth0",
    observability: "otel-datadog",
    confidence: 0.86,
    rationale: "Data-heavy profile with efficient backend processing and traceability.",
    tradeoffs: { cost: "high", complexity: "high", lockIn: "medium", compliance: "high" },
  },
};

const DEFAULT_ARCHITECTURE_PRESETS = {
  runtime: ["node", "python", "java"] as const,
  database: ["postgres", "supabase", "mongodb"] as const,
  deployment: ["docker", "vercel", "cloudflare", "aws"] as const,
};

type ArchitecturePresets = {
  runtime: Array<FoundryV2Draft["runtime"]>;
  database: Array<FoundryV2Draft["database"]>;
  deployment: Array<FoundryV2Draft["deployment"]>;
};

const RUNTIME_LABELS: Record<FoundryV2Draft["runtime"], string> = {
  node: "Node.js",
  python: "Python",
  java: "Java",
};

const DATABASE_LABELS: Record<FoundryV2Draft["database"], string> = {
  postgres: "PostgreSQL",
  supabase: "Supabase",
  mongodb: "MongoDB",
};

const DEPLOYMENT_LABELS: Record<FoundryV2Draft["deployment"], string> = {
  docker: "Docker",
  vercel: "Vercel",
  cloudflare: "Cloudflare",
  aws: "AWS",
};

const RUNTIME_PROFILE_OPTIONS: Array<{
  id: RuntimeProfileId;
  label: string;
  summary: string;
  stackHint: string;
  controlHeadline: string;
}> = [
  {
    id: "local",
    label: "Local sandbox",
    summary: "Rehearsal-only lane for local validation. Not promotable to a real customer runtime.",
    stackHint: "Docker + local secrets + seeded data",
    controlHeadline: "Dry-run only, no live mutation.",
  },
  {
    id: "local_by_design",
    label: "Local by design",
    summary: "Production stays on sovereign local infrastructure with explicit bootstrap, backup, and rollback.",
    stackHint: "Docker + local data plane + local runbook",
    controlHeadline: "IAM, backup, retention, rollback must be explicit.",
  },
  {
    id: "customer_server",
    label: "Customer server",
    summary: "Customer-managed server or cloud account with an explicit repo, support lane, and observability.",
    stackHint: "Existing repo + Docker/AWS + enterprise identity",
    controlHeadline: "Repository, IAM, secrets, audit, and rollback stay blocking.",
  },
  {
    id: "cloud_managed",
    label: "Managed cloud",
    summary: "Managed runtime on Vercel, Cloudflare, or AWS with governed delivery and promotion.",
    stackHint: "Managed deployment + external repo + governed release lane",
    controlHeadline: "Provider scopes, secrets, observability, rollback remain explicit.",
  },
  {
    id: "regulated_hybrid",
    label: "Regulated hybrid",
    summary: "Customer-controlled data plane plus governed managed control plane for sponsor-grade regulated delivery.",
    stackHint: "Existing repo + enterprise identity + AWS/customer perimeter",
    controlHeadline: "IAM, audit, backup, retention, and rollback all become release blockers.",
  },
];

const INTEGRATION_OPTIONS: Array<{ id: IntegrationTarget; label: string; envHint: string }> = [
  { id: "openai", label: "OpenAI", envHint: "OPENAI_API_KEY" },
  { id: "supabase", label: "Supabase API", envHint: "SUPABASE_URL, SUPABASE_SERVICE_ROLE_KEY" },
  { id: "google-maps", label: "Google Maps", envHint: "GOOGLE_MAPS_API_KEY" },
  { id: "erp", label: "ERP connector", envHint: "ERP_BASE_URL, ERP_API_KEY" },
  { id: "stripe", label: "Stripe", envHint: "STRIPE_SECRET_KEY, STRIPE_WEBHOOK_SECRET" },
  { id: "redis", label: "Redis", envHint: "REDIS_URL" },
  { id: "smtp", label: "SMTP", envHint: "SMTP_HOST, SMTP_USER, SMTP_PASSWORD" },
  { id: "s3", label: "S3 storage", envHint: "S3_BUCKET, S3_ACCESS_KEY_ID, S3_SECRET_ACCESS_KEY" },
];

function uniqueStrings(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach((value) => {
    const normalized = value.trim();
    if (!normalized) return;
    const key = normalized.toUpperCase();
    if (seen.has(key)) return;
    seen.add(key);
    result.push(normalized);
  });
  return result;
}

function parseEnvKeys(value: string): string[] {
  return uniqueStrings(
    value
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean),
  );
}

function parseContractList(value: string): string[] {
  return uniqueStrings(
    value
      .split(/\r?\n|;/)
      .map((item) => item.trim())
      .filter(Boolean),
  );
}

function extractTranscriptFallback(contextGate: FoundryV2ContextGateResponse): string {
  const transcripts = contextGate.context.bundle.meetingTranscripts ?? [];
  for (let i = transcripts.length - 1; i >= 0; i -= 1) {
    const transcript = transcripts[i] as { answeredQuestions?: Array<{ answer?: string }> } | undefined;
    const answered = transcript?.answeredQuestions ?? [];
    for (let j = answered.length - 1; j >= 0; j -= 1) {
      const response = answered[j];
      const answer = typeof response?.answer === "string" ? response.answer.trim() : "";
      if (answer.length >= 80) return answer;
    }
  }
  return "";
}

function buildAutoplanningTranscript(
  draft: FoundryV2Draft,
  projectName: string | null,
  contextGate: FoundryV2ContextGateResponse | null,
): string {
  const transcript = draft.transcriptText.trim() || (contextGate ? extractTranscriptFallback(contextGate) : "");
  if (transcript.length >= 80) return transcript;

  const label = projectName || draft.projectId.trim() || "the project";
  const goal = draft.businessGoal.trim() || `Deliver ${label} as a deterministic enterprise application.`;
  const facts = contextGate?.context.bundle.keyFacts?.facts?.slice(0, 3) ?? [];
  const assumptions = contextGate?.context.bundle.keyFacts?.assumptions?.slice(0, 2) ?? [];

  return [goal, ...facts, ...assumptions, "The delivery must stay governed, premium, and executable without reopening the brief once Jarvis starts."]
    .filter(Boolean)
    .join(" ");
}

function buildAutoplanningList(preferred: string[], fallback: string[]): string[] {
  return uniqueStrings(preferred.length ? preferred : fallback);
}

function isIntegrationTarget(value: unknown): value is IntegrationTarget {
  return INTEGRATION_OPTIONS.some((option) => option.id === value);
}

function deriveSuggestedEnvKeys(draft: FoundryV2Draft): string[] {
  const keys = ["APP_ENV", "PORT"];

  if (draft.database === "postgres") keys.push("DATABASE_URL");
  if (draft.database === "supabase") keys.push("SUPABASE_URL", "SUPABASE_ANON_KEY", "SUPABASE_SERVICE_ROLE_KEY");
  if (draft.database === "mongodb") keys.push("MONGODB_URI");

  if (draft.auth === "auth0") keys.push("AUTH0_DOMAIN", "AUTH0_CLIENT_ID", "AUTH0_CLIENT_SECRET");
  if (draft.auth === "clerk") keys.push("CLERK_PUBLISHABLE_KEY", "CLERK_SECRET_KEY");
  if (draft.auth === "cognito") keys.push("COGNITO_REGION", "COGNITO_USER_POOL_ID", "COGNITO_CLIENT_ID");
  if (draft.auth === "sso") keys.push("SSO_ISSUER_URL", "SSO_CLIENT_ID", "SSO_CLIENT_SECRET");

  if (draft.deployment === "vercel") keys.push("VERCEL_TOKEN", "VERCEL_PROJECT_ID");
  if (draft.deployment === "cloudflare") keys.push("CLOUDFLARE_API_TOKEN", "CLOUDFLARE_ACCOUNT_ID");
  if (draft.deployment === "aws") keys.push("AWS_REGION", "AWS_ACCESS_KEY_ID", "AWS_SECRET_ACCESS_KEY");

  if (draft.observability === "otel-sentry") keys.push("OTEL_EXPORTER_OTLP_ENDPOINT", "SENTRY_DSN");
  if (draft.observability === "otel-datadog") keys.push("OTEL_EXPORTER_OTLP_ENDPOINT", "DD_API_KEY", "DD_SITE");

  if (draft.githubMode === "existing-repo" || draft.githubMode === "create-repo") {
    keys.push("GITHUB_TOKEN");
  }

  draft.integrationTargets.forEach((integration) => {
    if (integration === "openai") keys.push("OPENAI_API_KEY");
    if (integration === "supabase") keys.push("SUPABASE_URL", "SUPABASE_SERVICE_ROLE_KEY");
    if (integration === "google-maps") keys.push("GOOGLE_MAPS_API_KEY");
    if (integration === "erp") keys.push("ERP_BASE_URL", "ERP_API_KEY");
    if (integration === "stripe") keys.push("STRIPE_SECRET_KEY", "STRIPE_WEBHOOK_SECRET");
    if (integration === "redis") keys.push("REDIS_URL");
    if (integration === "smtp") keys.push("SMTP_HOST", "SMTP_PORT", "SMTP_USER", "SMTP_PASSWORD");
    if (integration === "s3") keys.push("S3_BUCKET", "S3_REGION", "S3_ACCESS_KEY_ID", "S3_SECRET_ACCESS_KEY");
  });

  return uniqueStrings(keys);
}

function readSavedDraft(): FoundryV2Draft {
  if (typeof window === "undefined") return DEFAULT_DRAFT;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return DEFAULT_DRAFT;
  try {
    const parsed = JSON.parse(raw) as Partial<FoundryV2Draft>;
    const merged = { ...DEFAULT_DRAFT, ...parsed };
    const rawIntegrations = Array.isArray(parsed.integrationTargets) ? parsed.integrationTargets : [];
    return {
      ...merged,
      integrationTargets: rawIntegrations.filter(isIntegrationTarget),
    };
  } catch {
    return DEFAULT_DRAFT;
  }
}

export function FoundryV2Screen(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const [draft, setDraft] = useState<FoundryV2Draft>(() => readSavedDraft());
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [handoffMessage, setHandoffMessage] = useState<string | null>(null);
  const [handoffError, setHandoffError] = useState<string | null>(null);
  const [isHandoffSubmitting, setIsHandoffSubmitting] = useState(false);
  const [contextGate, setContextGate] = useState<FoundryV2ContextGateResponse | null>(null);
  const [contextError, setContextError] = useState<string | null>(null);
  const [isContextLoading, setIsContextLoading] = useState(false);
  const [contextRefreshKey, setContextRefreshKey] = useState<number>(0);
  const [resolvedProjectId, setResolvedProjectId] = useState<string>("");
  const [resolvedProjectName, setResolvedProjectName] = useState<string | null>(null);
  const [projectResolutionError, setProjectResolutionError] = useState<string | null>(null);
  const [isProjectResolving, setIsProjectResolving] = useState(false);
  const [architectureRecommendation, setArchitectureRecommendation] = useState<FoundryV2ArchitectureRecommendation | null>(null);
  const [isRecommendationLoading, setIsRecommendationLoading] = useState(false);
  const [recommendationError, setRecommendationError] = useState<string | null>(null);
  const [executionPlanPreview, setExecutionPlanPreview] = useState<FoundryV2ExecutionPlanPreview | null>(null);
  const [isExecutionPlanLoading, setIsExecutionPlanLoading] = useState(false);
  const [executionPlanError, setExecutionPlanError] = useState<string | null>(null);
  const [contractDraft, setContractDraft] = useState<FoundryContractV1DraftRecord | null>(null);
  const [isContractLoading, setIsContractLoading] = useState(false);
  const [isContractSubmitting, setIsContractSubmitting] = useState(false);
  const [contractError, setContractError] = useState<string | null>(null);
  const [contractMessage, setContractMessage] = useState<string | null>(null);
  const [contractValidationErrors, setContractValidationErrors] = useState<
    Array<{ field: string; code: string; message: string }>
  >([]);
  const [compositionDraft, setCompositionDraft] = useState<FoundryV2CompositionDraftRecord | null>(null);
  const [legacyReverseEngineering, setLegacyReverseEngineering] = useState<LongwallsLegacyReverseEngineeringPack | null>(null);
  const [legacyIntakeError, setLegacyIntakeError] = useState<string | null>(null);
  const [selectedLegacyRepoId, setSelectedLegacyRepoId] = useState<string>("");
  const [isLegacyPackLoading, setIsLegacyPackLoading] = useState(false);
  const [isLegacyIntakeLoading, setIsLegacyIntakeLoading] = useState(false);
  const [legacyIntakeResult, setLegacyIntakeResult] = useState<FoundryLegacyExternalIntakeResponse | null>(null);
  const [latestHandoff, setLatestHandoff] = useState<FoundryV2HandoffRecord | null>(null);
  const [pilotGate, setPilotGate] = useState<LongwallsEnterprisePilotGate | null>(null);
  const [buildReadyError, setBuildReadyError] = useState<string | null>(null);
  const [isBuildReadyLoading, setIsBuildReadyLoading] = useState(false);
  const [isCompositionSubmitting, setIsCompositionSubmitting] = useState(false);
  const [decisionEngine, setDecisionEngine] = useState<FoundryDecisionEngineRecord | null>(null);
  const [specValidator, setSpecValidator] = useState<FoundrySpecValidatorRecord | null>(null);
  const [decisionLedger, setDecisionLedger] = useState<FoundryDecisionLedgerRecord | null>(null);
  const [lecV2, setLecV2] = useState<FoundryV2LecV2Record | null>(null);
  const [deterministicAssembly, setDeterministicAssembly] = useState<FoundryV2DeterministicAssemblyRecord | null>(null);
  const [domainPacks, setDomainPacks] = useState<FoundryV2DomainPacksRecord | null>(null);
  const [organizationMemory, setOrganizationMemory] = useState<FoundryV2OrganizationMemoryRecord | null>(null);
  const [qaFactory, setQaFactory] = useState<FoundryV2QaFactoryRecord | null>(null);
  const [simulationEngine, setSimulationEngine] = useState<FoundryV2SimulationEngineRecord | null>(null);
  const [runtimeConnectors, setRuntimeConnectors] = useState<FoundryV2RuntimeConnectorsRecord | null>(null);
  const [jarvisEvaluation, setJarvisEvaluation] = useState<FoundryV2JarvisEvaluationRecord | null>(null);
  const [eclosiaBenchmark, setEclosiaBenchmark] = useState<FoundryV2EclosiaBenchmarkRecord | null>(null);
  const [runtimeRelease, setRuntimeRelease] = useState<FoundryV2RuntimeReleaseRecord | null>(null);
  const [traceability, setTraceability] = useState<FoundryV2TraceabilityRecord | null>(null);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [isDecisionLoading, setIsDecisionLoading] = useState(false);
  const [isDecisionMutating, setIsDecisionMutating] = useState(false);
  const [specValidatorError, setSpecValidatorError] = useState<string | null>(null);
  const [isSpecValidatorLoading, setIsSpecValidatorLoading] = useState(false);
  const [decisionLedgerError, setDecisionLedgerError] = useState<string | null>(null);
  const [isDecisionLedgerLoading, setIsDecisionLedgerLoading] = useState(false);
  const [deterministicAssemblyError, setDeterministicAssemblyError] = useState<string | null>(null);
  const [isDeterministicAssemblyLoading, setIsDeterministicAssemblyLoading] = useState(false);
  const [domainPacksError, setDomainPacksError] = useState<string | null>(null);
  const [isDomainPacksLoading, setIsDomainPacksLoading] = useState(false);
  const [organizationMemoryError, setOrganizationMemoryError] = useState<string | null>(null);
  const [isOrganizationMemoryLoading, setIsOrganizationMemoryLoading] = useState(false);
  const [qaFactoryError, setQaFactoryError] = useState<string | null>(null);
  const [isQaFactoryLoading, setIsQaFactoryLoading] = useState(false);
  const [simulationEngineError, setSimulationEngineError] = useState<string | null>(null);
  const [isSimulationEngineLoading, setIsSimulationEngineLoading] = useState(false);
  const [runtimeConnectorsError, setRuntimeConnectorsError] = useState<string | null>(null);
  const [isRuntimeConnectorsLoading, setIsRuntimeConnectorsLoading] = useState(false);
  const [jarvisEvaluationError, setJarvisEvaluationError] = useState<string | null>(null);
  const [isJarvisEvaluationLoading, setIsJarvisEvaluationLoading] = useState(false);
  const [eclosiaBenchmarkError, setEclosiaBenchmarkError] = useState<string | null>(null);
  const [isEclosiaBenchmarkLoading, setIsEclosiaBenchmarkLoading] = useState(false);
  const [runtimeReleaseError, setRuntimeReleaseError] = useState<string | null>(null);
  const [isRuntimeReleaseLoading, setIsRuntimeReleaseLoading] = useState(false);
  const [traceabilityError, setTraceabilityError] = useState<string | null>(null);
  const [isTraceabilityLoading, setIsTraceabilityLoading] = useState(false);
  const [buildReadyReloadNonce, setBuildReadyReloadNonce] = useState(0);
  const [isAutoplanningBootstrapLoading, setIsAutoplanningBootstrapLoading] = useState(false);
  const [architecturePresets, setArchitecturePresets] = useState<ArchitecturePresets>({
    runtime: [...DEFAULT_ARCHITECTURE_PRESETS.runtime],
    database: [...DEFAULT_ARCHITECTURE_PRESETS.database],
    deployment: [...DEFAULT_ARCHITECTURE_PRESETS.deployment],
  });

  const activeStep = STEPS[activeStepIndex];
  const localRecommendation = LOCAL_AI_RECOMMENDATIONS[draft.appType];
  const recommendation = architectureRecommendation ?? localRecommendation;
  const suggestedEnvKeys = useMemo(() => deriveSuggestedEnvKeys(draft), [draft]);
  const manualEnvKeys = useMemo(() => parseEnvKeys(draft.envKeys), [draft.envKeys]);
  const effectiveEnvKeys = useMemo(
    () => uniqueStrings([...suggestedEnvKeys, ...manualEnvKeys]),
    [manualEnvKeys, suggestedEnvKeys],
  );
  const selectedLegacyRepo = useMemo(
    () => legacyReverseEngineering?.repos.find((repo) => repo.id === selectedLegacyRepoId) ?? null,
    [legacyReverseEngineering, selectedLegacyRepoId],
  );

  const readinessChecks = useMemo(
    () => [
      {
        id: "intent",
        label: "Intent is complete",
        ok: Boolean(resolvedProjectId && draft.appName.trim() && draft.businessGoal.trim()),
      },
      {
        id: "context_gate",
        label: "Context readiness gate is green",
        ok: contextGate?.readiness?.decision === "pass",
      },
      {
        id: "contract_v1",
        label: "Foundry Contract v1 gate is green",
        ok: contractDraft?.assessment?.isReadyForSpec === true,
      },
      {
        id: "architecture",
        label: "Architecture choices are locked",
        ok: Boolean(draft.runtime && draft.backend && draft.database && draft.deployment),
      },
      {
        id: "environment",
        label: "Environment contract is provided",
        ok: Boolean(draft.runtimeProfile && draft.githubMode && draft.complianceProfile && effectiveEnvKeys.length > 0),
      },
      {
        id: "decision_engine",
        label: "Critical design decisions are promoted",
        ok: (decisionEngine?.buildSummary.blockerSectionIds.length ?? 0) === 0,
      },
      {
        id: "spec_validator",
        label: "Spec validator is green",
        ok: specValidator?.report.summary.status === "ready",
      },
      {
        id: "qa_factory",
        label: "QA factory is promotable",
        ok: qaFactory?.report.summary.canPromote === true,
      },
      {
        id: "simulation_engine",
        label: "Simulation engine is promotable",
        ok: simulationEngine?.report.summary.canPromote === true,
      },
      {
        id: "delivery_graph",
        label: "Delivery graph is promotable",
        ok: (traceability?.deliveryGraph?.summary.canPromote ?? traceability?.traceability.summary.canPromote) === true,
      },
      {
        id: "lec_v2",
        label: "LEC v2 is compiled and launchable",
        ok: decisionLedger?.decisionLedger.readiness.canCompileLecV2 === true && lecV2?.lec.executionControl.canLaunchJarvis === true,
      },
      {
        id: "autoplanning",
        label: "Autonomous planning is enabled",
        ok: draft.autoPlanning,
      },
    ],
    [
      contextGate?.readiness?.decision,
      contractDraft?.assessment?.isReadyForSpec,
      decisionLedger?.decisionLedger.readiness.canCompileLecV2,
      decisionEngine?.buildSummary.blockerSectionIds.length,
      qaFactory?.report.summary.canPromote,
      simulationEngine?.report.summary.canPromote,
      specValidator?.report.summary.status,
      traceability?.deliveryGraph?.summary.canPromote,
      traceability?.traceability.summary.canPromote,
      draft,
      effectiveEnvKeys.length,
      lecV2?.lec.executionControl.canLaunchJarvis,
      resolvedProjectId,
    ],
  );

  const isReadyForHandoff = readinessChecks.every((item) => item.ok);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  }, [draft]);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const projectIdFromQuery = search.get("projectId");
    if (!projectIdFromQuery || projectIdFromQuery === draft.projectId) return;
    setDraft((previous) => ({ ...previous, projectId: projectIdFromQuery }));
  }, [draft.projectId, location.search]);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const legacyRepoIdFromQuery = search.get("legacyRepoId")?.trim() ?? "";
    if (!legacyRepoIdFromQuery || legacyRepoIdFromQuery === selectedLegacyRepoId) return;
    setSelectedLegacyRepoId(legacyRepoIdFromQuery);
  }, [location.search, selectedLegacyRepoId]);

  useEffect(() => {
    let cancelled = false;
    setIsLegacyPackLoading(true);

    void fetchLongwallsLegacyReverseEngineeringPack()
      .then((payload) => {
        if (cancelled) return;
        setLegacyReverseEngineering(payload);
        setLegacyIntakeError(null);
        setSelectedLegacyRepoId((current) => {
          if (current && payload.repos.some((repo) => repo.id === current)) {
            return current;
          }
          if (payload.repos.some((repo) => repo.id === "conciergerie-2-0")) {
            return "conciergerie-2-0";
          }
          return payload.repos[0]?.id ?? "";
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setLegacyReverseEngineering(null);
        setLegacyIntakeError(error instanceof Error ? error.message : "Failed to load legacy reverse-engineering pack");
      })
      .finally(() => {
        if (cancelled) return;
        setIsLegacyPackLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const projectReference = draft.projectId.trim();
    if (!projectReference) {
      setResolvedProjectId("");
      setResolvedProjectName(null);
      setProjectResolutionError(null);
      setIsProjectResolving(false);
      return;
    }

    let isCancelled = false;
    setIsProjectResolving(true);
    const timeoutId = window.setTimeout(() => {
      void resolveProjectReference(projectReference)
        .then((resolved) => {
          if (isCancelled) return;
          setResolvedProjectId(resolved.projectId);
          setResolvedProjectName(resolved.projectName);
          setProjectResolutionError(null);
        })
        .catch((error) => {
          if (isCancelled) return;
          setResolvedProjectId("");
          setResolvedProjectName(null);
          setProjectResolutionError(error instanceof Error ? error.message : "Unable to resolve project reference");
        })
        .finally(() => {
          if (isCancelled) return;
          setIsProjectResolving(false);
        });
    }, 300);

    return () => {
      isCancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [draft.projectId]);

  const loadContextGate = useCallback(
    async (projectId: string) => {
      if (!projectId.trim()) {
        setContextError("Project name or ID is required to load discovery context.");
        setContextGate(null);
        return;
      }

      setIsContextLoading(true);
      setContextError(null);
      try {
        const payload = await fetchFoundryV2ContextGate(projectId.trim());
        setContextGate(payload);
        setDraft((previous) => {
          if (previous.transcriptText.trim()) return previous;
          const fallback = extractTranscriptFallback(payload);
          if (!fallback) return previous;
          return { ...previous, transcriptText: fallback };
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to load context gate";
        setContextError(message);
        setContextGate(null);
      } finally {
        setIsContextLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!resolvedProjectId) {
      setContextGate(null);
      return;
    }
    void loadContextGate(resolvedProjectId);
  }, [resolvedProjectId, loadContextGate, contextRefreshKey]);

  useEffect(() => {
    const projectId = resolvedProjectId;
    if (!projectId) {
      setContractDraft(null);
      setContractError(null);
      setContractMessage(null);
      setContractValidationErrors([]);
      setIsContractLoading(false);
      return;
    }

    setIsContractLoading(true);
    setContractError(null);

    void fetchLatestFoundryV2ContractV1Draft(projectId)
      .then((latestDraft) => {
        setContractDraft(latestDraft);
        if (!latestDraft) return;
        setDraft((previous) => {
          if (projectId !== latestDraft.projectId) return previous;
          return {
            ...previous,
            transcriptText: previous.transcriptText.trim() ? previous.transcriptText : latestDraft.contract.transcript.text,
            transcriptSource: previous.transcriptSource.trim()
              ? previous.transcriptSource
              : latestDraft.contract.transcript.source,
            transcriptLanguage: previous.transcriptLanguage.trim()
              ? previous.transcriptLanguage
              : latestDraft.contract.transcript.language ?? "",
            hypotheses: previous.hypotheses.trim() ? previous.hypotheses : latestDraft.contract.hypotheses.join("\n"),
            openQuestions: previous.openQuestions.trim()
              ? previous.openQuestions
              : latestDraft.contract.openQuestions.join("\n"),
            mvpScope: previous.mvpScope.trim() ? previous.mvpScope : latestDraft.contract.mvpScope.join("\n"),
            nfr: previous.nfr.trim() ? previous.nfr : latestDraft.contract.nfr.join("\n"),
            risks: previous.risks.trim() ? previous.risks : latestDraft.contract.risks.join("\n"),
            kpis: previous.kpis.trim() ? previous.kpis : latestDraft.contract.kpis.join("\n"),
          };
        });
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : "Failed to load latest Foundry Contract v1 draft";
        setContractError(message);
        setContractDraft(null);
      })
      .finally(() => {
        setIsContractLoading(false);
      });
  }, [resolvedProjectId]);

  useEffect(() => {
    const projectId = resolvedProjectId;
    if (!projectId) {
      setArchitectureRecommendation(null);
      setRecommendationError(null);
      setIsRecommendationLoading(false);
      setExecutionPlanPreview(null);
      setExecutionPlanError(null);
      setIsExecutionPlanLoading(false);
      return;
    }

    setIsRecommendationLoading(true);
    setRecommendationError(null);
    setIsExecutionPlanLoading(true);
    setExecutionPlanError(null);

    void fetchFoundryV2ArchitectureRecommendation(projectId, draft.appType)
      .then((payload) => {
        setArchitectureRecommendation(payload.recommendation);
        setArchitecturePresets({
          runtime: payload.presets.runtime,
          database: payload.presets.database,
          deployment: payload.presets.deployment,
        });
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : "Failed to load architecture recommendation";
        setArchitectureRecommendation(null);
        setArchitecturePresets({
          runtime: [...DEFAULT_ARCHITECTURE_PRESETS.runtime],
          database: [...DEFAULT_ARCHITECTURE_PRESETS.database],
          deployment: [...DEFAULT_ARCHITECTURE_PRESETS.deployment],
        });
        setRecommendationError(message);
      })
      .finally(() => {
        setIsRecommendationLoading(false);
      });

    void fetchFoundryV2ExecutionPlanPreview(projectId, draft.appType)
      .then((plan) => {
        setExecutionPlanPreview(plan);
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : "Failed to load execution plan preview";
        setExecutionPlanPreview(null);
        setExecutionPlanError(message);
      })
      .finally(() => {
        setIsExecutionPlanLoading(false);
      });
  }, [draft.appType, resolvedProjectId]);

  const loadDecisionArtifacts = useCallback(async (projectId: string) => {
    if (!projectId.trim()) {
      setSpecValidator(null);
      setDecisionLedger(null);
      setLecV2(null);
      setDeterministicAssembly(null);
      setOrganizationMemory(null);
      setQaFactory(null);
      setSimulationEngine(null);
      setRuntimeConnectors(null);
      setJarvisEvaluation(null);
      setEclosiaBenchmark(null);
      setRuntimeRelease(null);
      setTraceability(null);
      setSpecValidatorError(null);
      setDecisionLedgerError(null);
      setDeterministicAssemblyError(null);
      setOrganizationMemoryError(null);
      setQaFactoryError(null);
      setSimulationEngineError(null);
      setRuntimeConnectorsError(null);
      setJarvisEvaluationError(null);
      setEclosiaBenchmarkError(null);
      setRuntimeReleaseError(null);
      setTraceabilityError(null);
      setIsSpecValidatorLoading(false);
      setIsDecisionLedgerLoading(false);
      setIsDeterministicAssemblyLoading(false);
      setIsOrganizationMemoryLoading(false);
      setIsQaFactoryLoading(false);
      setIsSimulationEngineLoading(false);
      setIsRuntimeConnectorsLoading(false);
      setIsJarvisEvaluationLoading(false);
      setIsEclosiaBenchmarkLoading(false);
      setIsRuntimeReleaseLoading(false);
      setIsTraceabilityLoading(false);
      return;
    }

    setIsSpecValidatorLoading(true);
    setIsDecisionLedgerLoading(true);
    setIsDeterministicAssemblyLoading(true);
    setIsDomainPacksLoading(true);
    setIsOrganizationMemoryLoading(true);
    setIsQaFactoryLoading(true);
    setIsSimulationEngineLoading(true);
    setIsRuntimeConnectorsLoading(true);
    setIsJarvisEvaluationLoading(true);
    setIsEclosiaBenchmarkLoading(true);
    setIsRuntimeReleaseLoading(true);
    setIsTraceabilityLoading(true);
    setSpecValidatorError(null);
    setDecisionLedgerError(null);
    setDeterministicAssemblyError(null);
    setDomainPacksError(null);
    setOrganizationMemoryError(null);
    setQaFactoryError(null);
    setSimulationEngineError(null);
    setRuntimeConnectorsError(null);
    setJarvisEvaluationError(null);
    setEclosiaBenchmarkError(null);
    setRuntimeReleaseError(null);
    setTraceabilityError(null);
    const resolveMessage = (reason: unknown, fallback: string): string =>
      reason instanceof Error ? reason.message : fallback;

    const loadArtifact = async <T,>(
      loader: () => Promise<T | null>,
      setValue: (value: T | null) => void,
      setError: (value: string | null) => void,
      setLoading: (value: boolean) => void,
      fallback: string,
    ): Promise<void> => {
      try {
        const value = await loader();
        setValue(value);
        setError(null);
      } catch (error) {
        setValue(null);
        setError(resolveMessage(error, fallback));
      } finally {
        setLoading(false);
      }
    };

    const loadPriorityArtifacts = async (): Promise<void> => {
      // QA Factory and Runtime Release are user-facing gates and share heavy dependencies with
      // traceability. Loading them first avoids competing artifact generation on fresh e2e data.
      await loadArtifact(
        () => fetchFoundryV2QaFactory(projectId),
        setQaFactory,
        setQaFactoryError,
        setIsQaFactoryLoading,
        "Failed to load the QA factory",
      );
      await loadArtifact(
        () => fetchFoundryV2RuntimeRelease(projectId),
        setRuntimeRelease,
        setRuntimeReleaseError,
        setIsRuntimeReleaseLoading,
        "Failed to load runtime release",
      );
      await Promise.allSettled([
        loadArtifact(
          () => fetchFoundrySpecValidator(projectId),
          setSpecValidator,
          setSpecValidatorError,
          setIsSpecValidatorLoading,
          "Failed to load the spec validator",
        ),
        loadArtifact(
          () => fetchFoundryDecisionLedger(projectId),
          setDecisionLedger,
          setDecisionLedgerError,
          setIsDecisionLedgerLoading,
          "Failed to load the decision ledger",
        ),
      ]);
    };

    const backgroundJobs = [
      fetchFoundryV2LecV2(projectId)
        .then((value) => {
          setLecV2(value);
        })
        .catch(() => {
          setLecV2(null);
        }),
      loadArtifact(
        () => fetchFoundryV2DeterministicAssembly(projectId),
        setDeterministicAssembly,
        setDeterministicAssemblyError,
        setIsDeterministicAssemblyLoading,
        "Failed to load deterministic assembly",
      ),
      loadArtifact(
        () => fetchFoundryV2DomainPacks(projectId),
        setDomainPacks,
        setDomainPacksError,
        setIsDomainPacksLoading,
        "Failed to load domain packs",
      ),
      loadArtifact(
        () => fetchFoundryV2OrganizationMemory(projectId),
        setOrganizationMemory,
        setOrganizationMemoryError,
        setIsOrganizationMemoryLoading,
        "Failed to load organization memory",
      ),
      loadArtifact(
        () => fetchFoundryV2SimulationEngine(projectId),
        setSimulationEngine,
        setSimulationEngineError,
        setIsSimulationEngineLoading,
        "Failed to load the simulation engine",
      ),
      loadArtifact(
        () => fetchFoundryV2RuntimeConnectors(projectId),
        setRuntimeConnectors,
        setRuntimeConnectorsError,
        setIsRuntimeConnectorsLoading,
        "Failed to load runtime connectors",
      ),
      loadArtifact(
        () => fetchFoundryV2JarvisEvaluation(projectId),
        setJarvisEvaluation,
        setJarvisEvaluationError,
        setIsJarvisEvaluationLoading,
        "Failed to load the Jarvis evaluation",
      ),
      loadArtifact(
        () => fetchFoundryV2EclosiaBenchmark(projectId),
        setEclosiaBenchmark,
        setEclosiaBenchmarkError,
        setIsEclosiaBenchmarkLoading,
        "Failed to load the Eclosia benchmark",
      ),
    ];

    await loadPriorityArtifacts();

    void Promise.allSettled([
      loadArtifact(
        () => fetchFoundryV2Traceability(projectId),
        setTraceability,
        setTraceabilityError,
        setIsTraceabilityLoading,
        "Failed to load traceability",
      ),
      ...backgroundJobs,
    ]);
  }, []);

  useEffect(() => {
    const projectId = resolvedProjectId;
    if (!projectId) {
      setCompositionDraft(null);
      setLatestHandoff(null);
      setPilotGate(null);
      setDecisionEngine(null);
      setSpecValidator(null);
      setDecisionLedger(null);
      setLecV2(null);
      setDeterministicAssembly(null);
      setDomainPacks(null);
      setOrganizationMemory(null);
      setQaFactory(null);
      setSimulationEngine(null);
      setRuntimeConnectors(null);
      setJarvisEvaluation(null);
      setRuntimeRelease(null);
      setTraceability(null);
      setDecisionError(null);
      setSpecValidatorError(null);
      setDecisionLedgerError(null);
      setDeterministicAssemblyError(null);
      setDomainPacksError(null);
      setOrganizationMemoryError(null);
      setQaFactoryError(null);
      setSimulationEngineError(null);
      setRuntimeConnectorsError(null);
      setJarvisEvaluationError(null);
      setRuntimeReleaseError(null);
      setTraceabilityError(null);
      setIsDecisionLoading(false);
      setIsSpecValidatorLoading(false);
      setIsDecisionLedgerLoading(false);
      setIsDeterministicAssemblyLoading(false);
      setIsDomainPacksLoading(false);
      setIsOrganizationMemoryLoading(false);
      setIsQaFactoryLoading(false);
      setIsSimulationEngineLoading(false);
      setIsRuntimeConnectorsLoading(false);
      setIsJarvisEvaluationLoading(false);
      setIsRuntimeReleaseLoading(false);
      setIsTraceabilityLoading(false);
      setBuildReadyError(null);
      setIsBuildReadyLoading(false);
      return;
    }

    let isCancelled = false;
    setIsBuildReadyLoading(true);
    setBuildReadyError(null);

    void (async () => {
      try {
        const [handoff, gate, seededComposition] = await Promise.all([
          fetchLatestFoundryV2Handoff(projectId).catch(() => null),
          fetchLongwallsPilotGate(projectId).catch(() => null),
          fetchLatestFoundryV2CompositionDraft(projectId).catch(() => null),
        ]);
        let composition = seededComposition;
        let latestContract = contractDraft;

        if (!latestContract) {
          latestContract = await fetchLatestFoundryV2ContractV1Draft(projectId).catch(() => null);
        }

        if (!composition && latestContract?.assessment?.isReadyForSpec) {
          composition = await generateCompositionDraft({ silent: true });
        }

        if (isCancelled) return;
        if (latestContract && latestContract.id !== contractDraft?.id) {
          setContractDraft(latestContract);
        }
        setLatestHandoff(handoff);
        setPilotGate(gate);
        setCompositionDraft(composition);
        if (composition) {
          setIsDecisionLoading(true);
          try {
            const nextDecisionEngine = await fetchFoundryDecisionEngine(projectId);
            if (isCancelled) return;
            setDecisionEngine(nextDecisionEngine);
            setDecisionError(null);
            await loadDecisionArtifacts(projectId);
          } catch (error) {
            if (isCancelled) return;
            const message = error instanceof Error ? error.message : "Failed to load Foundry decisions";
            setDecisionEngine(null);
            setSpecValidator(null);
            setDecisionLedger(null);
            setLecV2(null);
            setDeterministicAssembly(null);
            setOrganizationMemory(null);
            setQaFactory(null);
            setSimulationEngine(null);
            setRuntimeConnectors(null);
            setJarvisEvaluation(null);
            setRuntimeRelease(null);
            setTraceability(null);
            setDecisionError(message);
            setSpecValidatorError(message);
            setDecisionLedgerError(message);
            setDeterministicAssemblyError(message);
            setOrganizationMemoryError(message);
            setQaFactoryError(message);
            setSimulationEngineError(message);
            setRuntimeConnectorsError(message);
            setJarvisEvaluationError(message);
            setRuntimeReleaseError(message);
            setTraceabilityError(message);
          } finally {
            if (!isCancelled) {
              setIsDecisionLoading(false);
            }
          }
        } else {
          setDecisionEngine(null);
          setSpecValidator(null);
          setDecisionLedger(null);
          setLecV2(null);
          setDeterministicAssembly(null);
          setOrganizationMemory(null);
          setQaFactory(null);
          setSimulationEngine(null);
          setRuntimeConnectors(null);
          setJarvisEvaluation(null);
          setRuntimeRelease(null);
          setTraceability(null);
          setDecisionError(null);
          setSpecValidatorError(null);
          setDecisionLedgerError(null);
          setDeterministicAssemblyError(null);
          setOrganizationMemoryError(null);
          setQaFactoryError(null);
          setSimulationEngineError(null);
          setRuntimeConnectorsError(null);
          setJarvisEvaluationError(null);
          setRuntimeReleaseError(null);
          setTraceabilityError(null);
          setIsDecisionLoading(false);
          setIsSpecValidatorLoading(false);
          setIsDecisionLedgerLoading(false);
          setIsDeterministicAssemblyLoading(false);
          setIsOrganizationMemoryLoading(false);
          setIsQaFactoryLoading(false);
          setIsSimulationEngineLoading(false);
          setIsRuntimeConnectorsLoading(false);
          setIsJarvisEvaluationLoading(false);
          setIsRuntimeReleaseLoading(false);
          setIsTraceabilityLoading(false);
        }
      } catch (error) {
        if (isCancelled) return;
        const message = error instanceof Error ? error.message : "Failed to load build-ready status";
        setBuildReadyError(message);
      } finally {
        if (!isCancelled) {
          setIsBuildReadyLoading(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [buildReadyReloadNonce, contractDraft?.assessment?.isReadyForSpec, contractDraft?.id, loadDecisionArtifacts, resolvedProjectId]);

  const updateDraft = <K extends keyof FoundryV2Draft>(field: K, value: FoundryV2Draft[K]) => {
    setHandoffMessage(null);
    setHandoffError(null);
    setContractMessage(null);
    setContractError(null);
    setDraft((previous) => ({ ...previous, [field]: value }));
  };

  const toggleIntegrationTarget = useCallback((target: IntegrationTarget) => {
    setHandoffMessage(null);
    setHandoffError(null);
    setDraft((previous) => {
      const exists = previous.integrationTargets.includes(target);
      return {
        ...previous,
        integrationTargets: exists
          ? previous.integrationTargets.filter((item) => item !== target)
          : [...previous.integrationTargets, target],
      };
    });
  }, []);

  const applySuggestedEnvKeys = () => {
    updateDraft("envKeys", effectiveEnvKeys.join("\n"));
  };

  const applyAiRecommendation = () => {
    setHandoffMessage(null);
    setDraft((previous) => ({
      ...previous,
      runtime: recommendation.runtime,
      frontend: recommendation.frontend,
      backend: recommendation.backend,
      database: recommendation.database,
      deployment: recommendation.deployment,
      auth: recommendation.auth,
      observability: recommendation.observability,
    }));
  };

  const importLegacyRepoIntoFoundry = async () => {
    if (!resolvedProjectId) {
      setLegacyIntakeError("Project name or ID must resolve before importing a legacy repo.");
      return;
    }
    if (!selectedLegacyRepoId) {
      setLegacyIntakeError("Select a grounded legacy repo before importing.");
      return;
    }

    setIsLegacyIntakeLoading(true);
    setLegacyIntakeError(null);
    setContractError(null);
    setContractMessage(null);
    setHandoffError(null);
    setHandoffMessage(null);

    try {
      const result = await importLegacyRepoFoundryExternalIntake(resolvedProjectId, selectedLegacyRepoId);
      setLegacyIntakeResult(result);
      setContractDraft(result.contractDraft);
      setCompositionDraft(result.compositionDraft);
      setDraft((previous) => {
        const contract = result.contractDraft.contract;
        const environment = result.intake.blueprintInput.environment;
        const architecture = result.intake.blueprintInput.architecture;
        return {
          ...previous,
          projectId: resolvedProjectId,
          appName: result.intake.blueprintInput.appName,
          appType: result.intake.blueprintInput.appType as FoundryV2Draft["appType"],
          businessGoal: result.intake.blueprintInput.businessGoal,
          constraints: result.intake.blueprintInput.constraints ?? "",
          runtime: architecture.runtime as FoundryV2Draft["runtime"],
          frontend: architecture.frontend as FoundryV2Draft["frontend"],
          backend: architecture.backend as FoundryV2Draft["backend"],
          database: architecture.database as FoundryV2Draft["database"],
          deployment: architecture.deployment as FoundryV2Draft["deployment"],
          auth: architecture.auth as FoundryV2Draft["auth"],
          observability: architecture.observability as FoundryV2Draft["observability"],
          runtimeProfile: (environment.runtimeProfile ?? "customer_server") as FoundryV2Draft["runtimeProfile"],
          githubMode: environment.githubMode as FoundryV2Draft["githubMode"],
          repositoryName: environment.repositoryName ?? "",
          envKeys: uniqueStrings([...(environment.recommendedEnvKeys ?? []), ...environment.envKeys]).join("\n"),
          integrationTargets: (environment.integrationTargets ?? []).filter(isIntegrationTarget),
          integrationNotes: environment.integrationNotes ?? "",
          complianceProfile: environment.complianceProfile as FoundryV2Draft["complianceProfile"],
          autoPlanning: result.intake.blueprintInput.automation?.autoPlanning ?? true,
          transcriptText: contract.transcript.text,
          transcriptSource: contract.transcript.source,
          transcriptLanguage: contract.transcript.language ?? "",
          hypotheses: contract.hypotheses.join("\n"),
          openQuestions: contract.openQuestions.join("\n"),
          mvpScope: contract.mvpScope.join("\n"),
          nfr: contract.nfr.join("\n"),
          risks: contract.risks.join("\n"),
          kpis: contract.kpis.join("\n"),
        };
      });
      setContractMessage(`Legacy intake imported from ${result.intake.repositoryName}.`);
      setHandoffMessage(`Foundry contract and composition draft refreshed from legacy repo ${result.intake.repoSummary.title}.`);
      setContextRefreshKey((current) => current + 1);
      setBuildReadyReloadNonce((current) => current + 1);
    } catch (error) {
      setLegacyIntakeResult(null);
      setLegacyIntakeError(error instanceof Error ? error.message : "Failed to import legacy repo into Foundry");
    } finally {
      setIsLegacyIntakeLoading(false);
    }
  };

  const goNext = () => setActiveStepIndex((index) => Math.min(index + 1, STEPS.length - 1));
  const goBack = () => setActiveStepIndex((index) => Math.max(index - 1, 0));

  const buildContractPayload = () => ({
    transcriptText: draft.transcriptText.trim() || undefined,
    transcriptSource: draft.transcriptSource.trim() || undefined,
    transcriptLanguage: draft.transcriptLanguage.trim() || undefined,
    hypotheses: parseContractList(draft.hypotheses),
    openQuestions: parseContractList(draft.openQuestions),
    mvpScope: parseContractList(draft.mvpScope),
    nfr: parseContractList(draft.nfr),
    risks: parseContractList(draft.risks),
    kpis: parseContractList(draft.kpis),
    appType: draft.appType,
  });

  const buildAutoplanningContractPayload = useCallback(() => {
    const transcriptText = buildAutoplanningTranscript(draft, resolvedProjectName, contextGate);
    const factBundle = contextGate?.context.bundle.keyFacts;
    const executionStages = executionPlanPreview?.stages.map((stage) => stage.label) ?? [];

    const hypotheses = buildAutoplanningList(parseContractList(draft.hypotheses), [
      ...(factBundle?.assumptions ?? []),
      `${recommendation.runtime}/${recommendation.backend} with ${recommendation.database} is the current best-fit delivery spine.`,
      `${draft.complianceProfile} compliance remains a first-class constraint for this project.`,
      "Jarvis should only execute decisions that Foundry has explicitly promoted.",
    ]);

    const openQuestions = buildAutoplanningList(parseContractList(draft.openQuestions), [
      ...(factBundle?.unknowns ?? []),
      `Which production hosting posture should ${resolvedProjectName || draft.projectId || "this project"} lock before release?`,
      "Which integration or approval surface is the highest enterprise risk for the first release?",
    ]);

    const mvpScope = buildAutoplanningList(parseContractList(draft.mvpScope), [
      ...executionStages.slice(0, 4).map((stage) => `Deliver ${stage.toLowerCase()} as a deterministic release stage`),
      draft.businessGoal.trim() || `Deliver ${resolvedProjectName || draft.projectId || "the application"} with a governed primary workflow`,
    ]);

    const nfr = buildAutoplanningList(parseContractList(draft.nfr), [
      `Maintain ${draft.complianceProfile} compliance posture across runtime, QA, and handoff`,
      "Preserve desktop-first verification with Playwright, API checks, and evidence capture",
      "Keep auditability, rollback, and approval posture visible before Jarvis executes",
    ]);

    const risks = buildAutoplanningList(parseContractList(draft.risks), [
      ...(factBundle?.risks ?? []),
      "Unresolved stack decisions can create delivery drift if they stay open at handoff",
      "Integration ambiguity can widen the scope of the first enterprise slice",
    ]);

    const kpis = buildAutoplanningList(parseContractList(draft.kpis), [
      "Primary desktop flow runs end-to-end without manual workaround",
      "Critical design decisions are promoted before Jarvis execution",
      "Evidence, approvals, and rollback posture remain visible through release",
    ]);

    return {
      transcriptText,
      transcriptSource: draft.transcriptSource.trim() || "smartphone",
      transcriptLanguage: draft.transcriptLanguage.trim() || "fr",
      hypotheses,
      openQuestions,
      mvpScope,
      nfr,
      risks,
      kpis,
      appType: draft.appType,
    };
  }, [contextGate, draft, executionPlanPreview?.stages, recommendation.backend, recommendation.database, recommendation.runtime, resolvedProjectName]);

  const buildAutoplanningBlueprintPayload = useCallback(
    (latestContract: FoundryContractV1DraftRecord | null) => ({
      appName: draft.appName.trim() || resolvedProjectName || draft.projectId.trim() || "LongWalls application",
      appType: draft.appType,
      businessGoal:
        draft.businessGoal.trim() ||
        latestContract?.specDraft.summary ||
        `Deliver ${resolvedProjectName || draft.projectId.trim() || "this application"} with deterministic enterprise controls.`,
      constraints:
        draft.constraints.trim() ||
        "Keep the generated app premium, secure, deterministic, and enterprise-audit friendly.",
      architecture: {
        runtime: draft.runtime,
        frontend: draft.frontend,
        backend: draft.backend,
        database: draft.database,
        deployment: draft.deployment,
        auth: draft.auth,
        observability: draft.observability,
      },
      environment: {
        runtimeProfile: draft.runtimeProfile,
        githubMode: draft.githubMode,
        repositoryName: draft.repositoryName.trim(),
        envKeys: effectiveEnvKeys,
        recommendedEnvKeys: suggestedEnvKeys,
        integrationTargets: draft.integrationTargets,
        integrationNotes: draft.integrationNotes.trim(),
        complianceProfile: draft.complianceProfile,
      },
      automation: {
        autoPlanning: draft.autoPlanning,
      },
    }),
    [
      draft.appName,
      draft.appType,
      draft.auth,
      draft.autoPlanning,
      draft.backend,
      draft.businessGoal,
      draft.complianceProfile,
      draft.constraints,
      draft.database,
      draft.deployment,
      draft.frontend,
      draft.githubMode,
      draft.integrationNotes,
      draft.integrationTargets,
      draft.observability,
      draft.projectId,
      draft.repositoryName,
      draft.runtimeProfile,
      draft.runtime,
      effectiveEnvKeys,
      resolvedProjectName,
      suggestedEnvKeys,
    ],
  );

  const buildBlueprintPayload = () => ({
    appName: draft.appName.trim(),
    appType: draft.appType,
    businessGoal: draft.businessGoal.trim(),
    constraints: draft.constraints.trim(),
    architecture: {
      runtime: draft.runtime,
      frontend: draft.frontend,
      backend: draft.backend,
      database: draft.database,
      deployment: draft.deployment,
      auth: draft.auth,
      observability: draft.observability,
    },
    environment: {
      runtimeProfile: draft.runtimeProfile,
      githubMode: draft.githubMode,
      repositoryName: draft.repositoryName.trim(),
      envKeys: effectiveEnvKeys,
      recommendedEnvKeys: suggestedEnvKeys,
      integrationTargets: draft.integrationTargets,
      integrationNotes: draft.integrationNotes.trim(),
      complianceProfile: draft.complianceProfile,
    },
    automation: {
      autoPlanning: draft.autoPlanning,
    },
  });

  async function generateCompositionDraft(options: { silent?: boolean } = {}) {
    if (!resolvedProjectId) {
      if (!options.silent) {
        setBuildReadyError("Project name or ID must resolve before generating Foundry Contract v2.");
      }
      return null;
    }

    setIsCompositionSubmitting(true);
    if (!options.silent) {
      setBuildReadyError(null);
    }

    try {
      const created = await createFoundryV2CompositionDraft(resolvedProjectId, buildBlueprintPayload());
      setCompositionDraft(created);
      const refreshedDecisionEngine = await refreshFoundryDecisionEngine(resolvedProjectId);
      setDecisionEngine(refreshedDecisionEngine);
      setDecisionError(null);
      return created;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to generate Foundry Contract v2";
      setBuildReadyError(message);
      return null;
    } finally {
      setIsCompositionSubmitting(false);
    }
  }

  const ensureFoundryDecisionRuntime = useCallback(async () => {
    if (!resolvedProjectId || !draft.autoPlanning || isAutoplanningBootstrapLoading) {
      return;
    }

    setIsAutoplanningBootstrapLoading(true);
    try {
      let nextContract = contractDraft;
      if (!nextContract) {
        nextContract = await createFoundryV2ContractV1Draft(resolvedProjectId, buildAutoplanningContractPayload());
        setContractDraft(nextContract);
        setContractMessage(`Auto-generated contract draft ${nextContract.id} from discovery context.`);
        setContractValidationErrors([]);
        setContractError(null);
      }

      let nextComposition = compositionDraft ?? (await fetchLatestFoundryV2CompositionDraft(resolvedProjectId).catch(() => null));
      if (!nextComposition && nextContract?.assessment?.isReadyForSpec) {
        nextComposition = await createFoundryV2CompositionDraft(
          resolvedProjectId,
          buildAutoplanningBlueprintPayload(nextContract),
        );
        setCompositionDraft(nextComposition);
      }

      if (nextComposition) {
        const refreshedDecisionEngine = await refreshFoundryDecisionEngine(resolvedProjectId);
        setDecisionEngine(refreshedDecisionEngine);
        setDecisionError(null);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to bootstrap Foundry decision engine";
      setBuildReadyError(message);
    } finally {
      setIsAutoplanningBootstrapLoading(false);
    }
  }, [
    buildAutoplanningBlueprintPayload,
    buildAutoplanningContractPayload,
    compositionDraft,
    contractDraft,
    draft.autoPlanning,
    isAutoplanningBootstrapLoading,
    resolvedProjectId,
  ]);

  useEffect(() => {
    if (!resolvedProjectId || !draft.autoPlanning) return;
    if (isProjectResolving || isContextLoading || isContractLoading || isBuildReadyLoading || isCompositionSubmitting) return;
    if (!contextGate) return;
    if (decisionEngine) return;
    void ensureFoundryDecisionRuntime();
  }, [
    contextGate,
    decisionEngine,
    draft.autoPlanning,
    ensureFoundryDecisionRuntime,
    isBuildReadyLoading,
    isCompositionSubmitting,
    isContextLoading,
    isContractLoading,
    isProjectResolving,
    resolvedProjectId,
  ]);

  const generateContractAndSpecDraft = async () => {
    if (!resolvedProjectId) {
      setContractError("Project name or ID must resolve before generating Foundry Contract v1.");
      return;
    }

    setIsContractSubmitting(true);
    setContractError(null);
    setContractMessage(null);
    setContractValidationErrors([]);

    try {
      const created = await createFoundryV2ContractV1Draft(resolvedProjectId, buildContractPayload());
      let nextMessage = `Contract draft ${created.id} generated. Confidence ${(created.assessment.confidenceScore * 100).toFixed(0)}%.`;
      if (created.assessment.isReadyForSpec) {
        const composition = await generateCompositionDraft({ silent: true });
        if (composition) {
          nextMessage = `${nextMessage} Composition ${composition.id} refreshed.`;
        }
      }
      setContractDraft(created);
      setContractMessage(nextMessage);
      setDraft((previous) => ({
        ...previous,
        transcriptText: created.contract.transcript.text,
        transcriptSource: created.contract.transcript.source,
        transcriptLanguage: created.contract.transcript.language ?? "",
        hypotheses: created.contract.hypotheses.join("\n"),
        openQuestions: created.contract.openQuestions.join("\n"),
        mvpScope: created.contract.mvpScope.join("\n"),
        nfr: created.contract.nfr.join("\n"),
        risks: created.contract.risks.join("\n"),
        kpis: created.contract.kpis.join("\n"),
      }));
    } catch (error) {
      if (error instanceof FoundryV2ContractV1Error) {
        setContractValidationErrors(error.validationErrors ?? []);
      }
      const message = error instanceof Error ? error.message : "Failed to generate Foundry Contract v1 draft";
      setContractError(message);
    } finally {
      setIsContractSubmitting(false);
    }
  };

  const refreshDecisionPanel = async () => {
    if (!resolvedProjectId || !compositionDraft) return;
    setIsDecisionLoading(true);
    setDecisionError(null);
    try {
      const refreshed = await refreshFoundryDecisionEngine(resolvedProjectId);
      setDecisionEngine(refreshed);
      const latestComposition = await fetchLatestFoundryV2CompositionDraft(resolvedProjectId).catch(() => null);
      if (latestComposition) {
        setCompositionDraft(latestComposition);
      }
      await loadDecisionArtifacts(resolvedProjectId);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to refresh Foundry decisions";
      setDecisionError(message);
    } finally {
      setIsDecisionLoading(false);
    }
  };

  const discussDecisionSection = async (sectionId: string, content: string) => {
    if (!resolvedProjectId || !compositionDraft) return;
    setIsDecisionMutating(true);
    setDecisionError(null);
    try {
      const nextEngine = await createFoundryDecisionDiscussion(resolvedProjectId, sectionId, { content, author: "user" });
      setDecisionEngine(nextEngine);
      const latestComposition = await fetchLatestFoundryV2CompositionDraft(resolvedProjectId).catch(() => null);
      if (latestComposition) {
        setCompositionDraft(latestComposition);
      }
      await loadDecisionArtifacts(resolvedProjectId);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to discuss the decision section";
      setDecisionError(message);
    } finally {
      setIsDecisionMutating(false);
    }
  };

  const promoteDecisionSection = async (sectionId: string, optionId: string) => {
    if (!resolvedProjectId || !compositionDraft) return;
    setIsDecisionMutating(true);
    setDecisionError(null);
    try {
      const nextEngine = await promoteFoundryDecision(resolvedProjectId, sectionId, {
        optionId,
        promotedBy: "human",
      });
      setDecisionEngine(nextEngine);
      const latestComposition = await fetchLatestFoundryV2CompositionDraft(resolvedProjectId).catch(() => null);
      if (latestComposition) {
        setCompositionDraft(latestComposition);
      }
      await loadDecisionArtifacts(resolvedProjectId);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to promote the decision";
      setDecisionError(message);
    } finally {
      setIsDecisionMutating(false);
    }
  };

  const handoffToJarvisV2 = async () => {
    if (!isReadyForHandoff || !resolvedProjectId) return;

    setIsHandoffSubmitting(true);
    setHandoffError(null);
    setHandoffMessage(null);
    try {
      const composition = compositionDraft ?? (await generateCompositionDraft());
      if (!composition) {
        throw new Error("Foundry Contract v2 must be available before handing off to Jarvis.");
      }
      const handoff = await createFoundryV2Handoff(resolvedProjectId, buildBlueprintPayload());

      window.localStorage.setItem(
        "longwalls.v2.handoffPayload",
        JSON.stringify({
          projectId: handoff.projectId,
          handoffId: handoff.id,
          appName: handoff.input.appName,
          appType: handoff.input.appType,
          archetypeId: composition.assemblyPlan.archetypeId,
          brickIds: composition.assemblyPlan.brickIds,
          generatedAt: handoff.createdAt,
          status: handoff.status,
        }),
      );

      setHandoffMessage(`Handoff ${handoff.id} created and locked for Jarvis V2.`);
      setLatestHandoff(handoff);
      navigate(`/jarvis?projectId=${encodeURIComponent(handoff.projectId)}`);
    } catch (error) {
      if (error instanceof FoundryV2HandoffError && error.readiness) {
        setContextGate((previous) => {
          if (!previous) return null;
          return { ...previous, readiness: error.readiness as any };
        });
      }
      if (error instanceof FoundryV2HandoffError && error.code === "FOUNDRY_DECISIONS_BLOCKED") {
        void refreshDecisionPanel();
      }
      const message = error instanceof Error ? error.message : "Failed to lock handoff";
      setHandoffError(message);
    } finally {
      setIsHandoffSubmitting(false);
    }
  };

  const buildReadyPanel = (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="foundry-build-ready-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">Build-ready + Forge capability</div>
          <div className="mt-1 text-xs text-slate-600">
            This panel is the deterministic handoff contract between Atlas outputs, Foundry artifacts, and Jarvis delivery.
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void generateCompositionDraft()}
            disabled={!resolvedProjectId || !contractDraft?.assessment?.isReadyForSpec || isCompositionSubmitting}
          >
            {isCompositionSubmitting ? "Refreshing contract v2..." : "Refresh contract v2"}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => resolvedProjectId && navigate(`/jarvis?projectId=${encodeURIComponent(resolvedProjectId)}`)}
            disabled={!resolvedProjectId}
          >
            Open Jarvis delivery
          </Button>
        </div>
      </div>

      {isBuildReadyLoading ? (
        <div className="mt-3 text-xs text-slate-600">Loading build-ready posture...</div>
      ) : null}
      {buildReadyError ? (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {buildReadyError}
        </div>
      ) : null}

      <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        <div className="rounded-md border border-slate-200 bg-white p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Contract v1</div>
          <div className="mt-2 text-sm font-semibold text-slate-900">
            {contractDraft?.assessment?.isReadyForSpec ? "Build-ready" : "Needs work"}
          </div>
          <div className="mt-1 text-xs text-slate-600">
            {contractDraft ? `Draft ${contractDraft.id}` : "No deterministic contract generated yet."}
          </div>
        </div>
        <div className="rounded-md border border-slate-200 bg-white p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Contract v2</div>
          <div className="mt-2 text-sm font-semibold text-slate-900">
            {compositionDraft ? compositionDraft.assemblyPlan.archetypeId : "Waiting for composition"}
          </div>
          <div className="mt-1 text-xs text-slate-600">
            {compositionDraft
              ? `${compositionDraft.assemblyPlan.brickIds.length} bricks | ${compositionDraft.assemblyPlan.deliveryMode}`
              : contractDraft?.assessment?.isReadyForSpec
                ? "Generate the composition contract to lock archetype, bricks, and quality gates."
                : "Contract v2 appears after v1 reaches build-ready."}
          </div>
        </div>
        <div className="rounded-md border border-slate-200 bg-white p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Forge handoff</div>
          <div className="mt-2 text-sm font-semibold text-slate-900">
            {latestHandoff ? latestHandoff.status : "Not handed off"}
          </div>
          <div className="mt-1 text-xs text-slate-600">
            {latestHandoff
              ? `${latestHandoff.specSectionCount} spec sections | planner ${latestHandoff.planner.stageCount} stages`
              : "Waiting for locked handoff package."}
          </div>
        </div>
        <div className="rounded-md border border-slate-200 bg-white p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Pilot gate</div>
          <div className="mt-2 text-sm font-semibold text-slate-900">
            {pilotGate ? `${pilotGate.status} (${pilotGate.score}/100)` : "Pending"}
          </div>
          <div className="mt-1 text-xs text-slate-600">
            {pilotGate
              ? `${pilotGate.summary.greenRuns}/${pilotGate.summary.requiredGreenRuns} green runs | rollback ${pilotGate.summary.rollbackReady ? "ready" : "missing"}`
              : "Pilot gate will appear once the project has crossed the first Jarvis proof run."}
          </div>
        </div>
        <div className="rounded-md border border-slate-200 bg-white p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Next move</div>
          <div className="mt-2 text-sm text-slate-700">
            {latestHandoff
              ? "Open Jarvis, review approvals and evidence, then launch or replay the pilot run."
              : compositionDraft
                ? "Lock the handoff package and push the assembly plan into Jarvis."
                : contractDraft?.assessment?.isReadyForSpec
                  ? "Generate contract v2, then lock the handoff package into Jarvis."
                  : "Close contract gaps until Foundry marks the project build-ready."}
          </div>
        </div>
      </div>

      {compositionDraft ? (
        <div className="mt-3 grid gap-3 lg:grid-cols-4">
          <div className="rounded-md border border-slate-200 bg-white p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Assembly plan</div>
            <div className="mt-2 text-sm font-semibold text-slate-900">{compositionDraft.assemblyPlan.archetypeId}</div>
            <div className="mt-1 text-xs text-slate-600">
              {compositionDraft.foundryContract.planningSummary.domainFamily} | {compositionDraft.foundryContract.planningSummary.operatingModel}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {compositionDraft.assemblyPlan.brickIds.map((brickId) => (
                <span
                  key={brickId}
                  className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-700"
                >
                  {brickId}
                </span>
              ))}
            </div>
          </div>
          <div className="rounded-md border border-slate-200 bg-white p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Planning summary</div>
            <div className="mt-2 text-sm font-semibold text-slate-900">
              {compositionDraft.foundryContract.planningSummary.status.toUpperCase()} • confidence{" "}
              {(compositionDraft.foundryContract.planningSummary.confidence * 100).toFixed(0)}%
            </div>
            <div className="mt-2 text-xs text-slate-600">
              Jarvis should execute this plan, not redesign it.
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {compositionDraft.foundryContract.planningSummary.moduleIds.map((moduleId) => (
                <span
                  key={moduleId}
                  className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700"
                >
                  {moduleId}
                </span>
              ))}
            </div>
          </div>
          <div className="rounded-md border border-slate-200 bg-white p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Quality gates</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {compositionDraft.assemblyPlan.qualityGates.map((gate) => (
                <span
                  key={gate}
                  className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] text-blue-700"
                >
                  {gate}
                </span>
              ))}
            </div>
          </div>
          <div className="rounded-md border border-slate-200 bg-white p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Security thesis</div>
            <div className="mt-2 text-sm text-slate-700">
              Audit {compositionDraft.foundryContract.securityThesis.auditLevel} | rollback{" "}
              {compositionDraft.foundryContract.securityThesis.rollbackRequired ? "required" : "optional"}
            </div>
            <div className="mt-2 text-xs text-slate-600">
              Roles: {compositionDraft.foundryContract.securityThesis.roles.join(", ") || "none"}
            </div>
          </div>
        </div>
      ) : null}

      {compositionDraft ? <FoundryAssemblyContractPanel compositionDraft={compositionDraft} /> : null}

      {compositionDraft ? (
        <div className="mt-3 grid gap-3 lg:grid-cols-[1.15fr_1fr_1fr]">
          <div className="rounded-md border border-slate-200 bg-white p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Service map</div>
            <div className="mt-2 text-xs text-slate-600">Required bricks and custom slices frozen before Jarvis starts.</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {compositionDraft.foundryContract.planningSummary.serviceIds.map((serviceId) => (
                <span
                  key={serviceId}
                  className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-700"
                >
                  {serviceId}
                </span>
              ))}
            </div>
            {compositionDraft.foundryContract.planningSummary.customSliceIds.length ? (
              <div className="mt-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Custom slices</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {compositionDraft.foundryContract.planningSummary.customSliceIds.map((sliceId) => (
                    <span
                      key={sliceId}
                      className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700"
                    >
                      {sliceId}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          <div className="rounded-md border border-slate-200 bg-white p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Primary flows</div>
            <div className="mt-2 text-xs text-slate-600">These flows become the canonical QA and acceptance lane.</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {compositionDraft.foundryContract.planningSummary.primaryFlowIds.map((flowId) => (
                <span
                  key={flowId}
                  className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] text-blue-700"
                >
                  {flowId}
                </span>
              ))}
            </div>
          </div>
          <div className="rounded-md border border-slate-200 bg-white p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Frozen artifacts</div>
            <div className="mt-2 text-xs text-slate-600">Open the normalized bundle if Jarvis needs evidence instead of new guesses.</div>
            <div className="mt-3 space-y-2 text-xs">
              <a className="font-medium text-blue-700 underline" href={compositionDraft.artifactUrls.foundryContract}>
                foundry_contract_v2.json
              </a>
              <a className="block font-medium text-blue-700 underline" href={compositionDraft.artifactUrls.assemblyPlan}>
                assembly_plan.json
              </a>
              <a className="block font-medium text-blue-700 underline" href={compositionDraft.artifactUrls.handoffContract}>
                handoff_contract.json
              </a>
              <a className="font-medium text-blue-700 underline" href={compositionDraft.artifactUrls.planningBundle}>
                solution_planning_bundle.json
              </a>
              <a className="block font-medium text-blue-700 underline" href={compositionDraft.artifactUrls.solutionPlan}>
                solution_plan.json
              </a>
              <a className="block font-medium text-blue-700 underline" href={compositionDraft.artifactUrls.serviceMap}>
                service_map.json
              </a>
              <a className="block font-medium text-blue-700 underline" href={compositionDraft.artifactUrls.deliveryPlan}>
                delivery_plan.json
              </a>
              <a className="block font-medium text-blue-700 underline" href={compositionDraft.artifactUrls.customDelta}>
                custom_delta_contract.json
              </a>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <FoundryV2AccessGuard
      projectId={resolvedProjectId}
      target="foundry-v2"
      isPending={Boolean(draft.projectId.trim()) && !resolvedProjectId && !projectResolutionError}
      onActivated={() => setBuildReadyReloadNonce((current) => current + 1)}
    >
      <div className="foundry-v2-screen mx-auto flex w-full max-w-[1400px] flex-col gap-6">
      <div className="foundry-v2-screen__intro rounded-xl border border-slate-200 bg-white/80 px-4 py-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Foundry</h1>
          <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
            Wizard
          </span>
        </div>
        <p className="mt-2 text-sm text-slate-600">
          Deterministic blueprint factory for enterprise delivery. Lock intent, architecture, environment, and handoff without reopening the brief once delivery starts.
        </p>
      </div>

      <div className="foundry-v2-screen__steps grid gap-3 md:grid-cols-4">
        {STEPS.map((step, index) => (
          <button
            key={step.id}
            type="button"
            className={`foundry-v2-screen__step rounded-lg border px-3 py-3 text-left transition ${
              activeStepIndex === index
                ? "border-blue-400 bg-blue-50"
                : "border-slate-200 bg-white hover:border-slate-300"
            }`}
            onClick={() => setActiveStepIndex(index)}
          >
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Step {index + 1}</div>
            <div className="mt-1 text-sm font-semibold text-slate-900">{step.label}</div>
            <div className="mt-1 text-xs text-slate-600">{step.description}</div>
          </button>
        ))}
      </div>

      <Card className="foundry-v2-screen__card border border-slate-200" bodyClassName="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-base font-semibold text-slate-900">{activeStep.label}</div>
            <div className="text-sm text-slate-600">{activeStep.description}</div>
          </div>
        </div>

        {buildReadyPanel}
        {resolvedProjectId ? (
          <FoundryDomainProfilePanel
            decisionEngine={decisionEngine}
            isLoading={isDecisionLoading || isAutoplanningBootstrapLoading}
          />
        ) : null}
        {resolvedProjectId ? (
          <FoundryDomainPacksPanel
            domainPacks={domainPacks}
            isLoading={isDomainPacksLoading || isAutoplanningBootstrapLoading}
            error={domainPacksError}
          />
        ) : null}
        {resolvedProjectId ? (
          <FoundryBrickSelectionPanel
            compositionDraft={compositionDraft}
            isLoading={isBuildReadyLoading || isCompositionSubmitting}
          />
        ) : null}
        {resolvedProjectId ? (
          <FoundryDecisionChecklistPanel
            decisionEngine={decisionEngine}
            isLoading={isDecisionLoading || isBuildReadyLoading || isCompositionSubmitting || isAutoplanningBootstrapLoading}
            error={decisionError}
            isMutating={isDecisionMutating || isAutoplanningBootstrapLoading}
            onRefresh={() => void refreshDecisionPanel()}
            onDiscuss={(sectionId, content) => discussDecisionSection(sectionId, content)}
            onPromote={(sectionId, optionId) => promoteDecisionSection(sectionId, optionId)}
          />
        ) : null}
        {resolvedProjectId ? (
          <FoundrySpecValidatorPanel
            specValidator={specValidator}
            isLoading={isSpecValidatorLoading || isAutoplanningBootstrapLoading}
            error={specValidatorError}
          />
        ) : null}
        {resolvedProjectId ? (
          <FoundryDecisionLedgerPanel
            decisionLedger={decisionLedger}
            lec={lecV2}
            isLoading={isDecisionLedgerLoading || isAutoplanningBootstrapLoading}
            error={decisionLedgerError}
          />
        ) : null}
        {resolvedProjectId ? (
          <FoundryDeterministicAssemblyPanel
            deterministicAssembly={deterministicAssembly}
            isLoading={isDeterministicAssemblyLoading || isAutoplanningBootstrapLoading}
            error={deterministicAssemblyError}
          />
        ) : null}
        {resolvedProjectId ? (
          <FoundryOrganizationMemoryPanel
            organizationMemory={organizationMemory}
            isLoading={isOrganizationMemoryLoading || isAutoplanningBootstrapLoading}
            error={organizationMemoryError}
          />
        ) : null}
        {resolvedProjectId ? (
          <FoundryQaFactoryPanel
            qaFactory={qaFactory}
            isLoading={isQaFactoryLoading || isAutoplanningBootstrapLoading}
            error={qaFactoryError}
          />
        ) : null}
        {resolvedProjectId ? (
          <FoundrySimulationEnginePanel
            simulationEngine={simulationEngine}
            isLoading={isSimulationEngineLoading || isAutoplanningBootstrapLoading}
            error={simulationEngineError}
          />
        ) : null}
        {resolvedProjectId ? (
          <FoundryRuntimeConnectorsPanel
            runtimeConnectors={runtimeConnectors}
            isLoading={isRuntimeConnectorsLoading || isAutoplanningBootstrapLoading}
            error={runtimeConnectorsError}
          />
        ) : null}
        {resolvedProjectId ? (
          <FoundryJarvisEvaluationPanel
            jarvisEvaluation={jarvisEvaluation}
            isLoading={isJarvisEvaluationLoading || isAutoplanningBootstrapLoading}
            error={jarvisEvaluationError}
          />
        ) : null}
        {resolvedProjectId ? (
          <FoundryEclosiaBenchmarkPanel
            benchmark={eclosiaBenchmark}
            isLoading={isEclosiaBenchmarkLoading || isAutoplanningBootstrapLoading}
            error={eclosiaBenchmarkError}
          />
        ) : null}
        {resolvedProjectId ? (
          <FoundryRuntimeReleasePanel
            runtimeRelease={runtimeRelease}
            isLoading={isRuntimeReleaseLoading || isAutoplanningBootstrapLoading}
            error={runtimeReleaseError}
          />
        ) : null}
        {resolvedProjectId ? (
          <FoundryTraceabilityPanel
            traceability={traceability}
            isLoading={isTraceabilityLoading || isAutoplanningBootstrapLoading}
            error={traceabilityError}
          />
        ) : null}
        {activeStep.id === "context" ? (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-[1fr_auto]">
              <label className="flex flex-col gap-1 text-sm text-slate-700">
                Project name (or ID) (required for context, brief, specs and handoff)
                <input
                  value={draft.projectId}
                  onChange={(event) => updateDraft("projectId", event.target.value)}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="Omnicane Logistics HQ project"
                />
              </label>
              <div className="flex items-end">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void loadContextGate(resolvedProjectId)}
                  disabled={isContextLoading || isProjectResolving || !resolvedProjectId}
                >
                  {isContextLoading ? "Refreshing context..." : "Refresh context gate"}
                </Button>
              </div>
            </div>

            {isProjectResolving ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                Resolving project reference...
              </div>
            ) : null}

            {resolvedProjectId ? (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                Resolved project: {resolvedProjectName || "(no name)"} ({resolvedProjectId})
              </div>
            ) : null}

            {projectResolutionError ? (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {projectResolutionError}
              </div>
            ) : null}

            {contextError ? (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {contextError}
              </div>
            ) : null}

            {contextGate ? (
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Discovery readiness</div>
                  <div className="mt-1 text-base font-semibold text-slate-900">
                    {contextGate.readiness.decision.toUpperCase()} - score {(contextGate.readiness.score * 100).toFixed(0)}%
                  </div>
                  <div className="mt-1 text-sm text-slate-600">
                    Status: {contextGate.readiness.status} | Open signals: {contextGate.readiness.signals.length}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Context assets</div>
                  <div className="mt-1 text-sm text-slate-700">
                    Meetings: {contextGate.context.bundle.meetingTranscripts?.length ?? 0}
                  </div>
                  <div className="text-sm text-slate-700">
                    Documents: {contextGate.context.bundle.documentAnalyses?.length ?? 0}
                  </div>
                  <div className="text-sm text-slate-700">
                    Notes: {contextGate.context.bundle.discovery?.discovery?.notes?.length ?? 0}
                  </div>
                </div>
              </div>
            ) : null}

            {contextGate?.readiness.signals?.length ? (
              <Card className="border border-amber-200" bodyClassName="space-y-2">
                <div className="text-sm font-semibold text-slate-900">Top readiness signals</div>
                <div className="space-y-1">
                  {contextGate.readiness.signals.slice(0, 5).map((signal) => (
                    <div key={signal.id} className="text-xs text-slate-700">
                      [{signal.severity}] {signal.title} - {signal.message}
                    </div>
                  ))}
                </div>
              </Card>
            ) : null}

            <Card
              className="border border-violet-200"
              bodyClassName="space-y-3"
              data-testid="foundry-legacy-external-intake-panel"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Legacy external intake</div>
                  <div className="text-xs text-slate-600">
                    Turn a grounded runtime into a deterministic Foundry opening instead of rewriting specs from memory.
                  </div>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void importLegacyRepoIntoFoundry()}
                  disabled={!resolvedProjectId || !selectedLegacyRepoId || isLegacyPackLoading || isLegacyIntakeLoading}
                >
                  {isLegacyIntakeLoading ? "Importing..." : "Import legacy repo into Foundry"}
                </Button>
              </div>

              {isLegacyPackLoading ? (
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  Loading grounded legacy repos...
                </div>
              ) : null}

              {legacyIntakeError ? (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{legacyIntakeError}</div>
              ) : null}

              <label className="flex flex-col gap-1 text-sm text-slate-700">
                Legacy repo
                <select
                  value={selectedLegacyRepoId}
                  onChange={(event) => setSelectedLegacyRepoId(event.target.value)}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
                >
                  <option value="">Select a grounded repo</option>
                  {(legacyReverseEngineering?.repos ?? []).map((repo) => (
                    <option key={repo.id} value={repo.id}>
                      {repo.title} ({repo.status})
                    </option>
                  ))}
                </select>
              </label>

              {selectedLegacyRepo ? (
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <div className="text-xs uppercase tracking-wide text-slate-500">Repo signal</div>
                    <div className="mt-1 text-base font-semibold text-slate-900">{selectedLegacyRepo.title}</div>
                    <div className="mt-1 text-sm text-slate-600">
                      Confidence {selectedLegacyRepo.foundryLift.confidenceScore}% | {selectedLegacyRepo.domainHypothesis.replace(/_/g, " ")}
                    </div>
                    <div className="mt-2 text-xs text-slate-600">{selectedLegacyRepo.sponsorNarrative}</div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <div className="text-xs uppercase tracking-wide text-slate-500">Foundry lift</div>
                    <div className="mt-1 text-sm text-slate-700">
                      Sections: {selectedLegacyRepo.foundryLift.sectionIds.join(", ") || "none"}
                    </div>
                    <div className="mt-1 text-sm text-slate-700">
                      Modules: {selectedLegacyRepo.extractedModules.slice(0, 4).map((module) => module.title).join(", ") || "none"}
                    </div>
                    <div className="mt-2 text-xs text-slate-600">{selectedLegacyRepo.reversePlan.firstExtraction}</div>
                  </div>
                </div>
              ) : null}

              {legacyIntakeResult ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                  Imported {legacyIntakeResult.intake.repositoryName}. Required env keys:{" "}
                  {legacyIntakeResult.intake.environment.requiredEnvKeys.join(", ") || "none"}.
                </div>
              ) : null}
            </Card>

            {resolvedProjectId ? (
              <ProjectPreDiscoveryPanel
                projectId={resolvedProjectId}
                contextRefreshKey={contextRefreshKey}
                onQuestionsUpdated={() => setContextRefreshKey((current) => current + 1)}
              />
            ) : (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                Enter a project name or ID to activate pre-discovery/discovery context modules.
              </div>
            )}

            <Card className="border border-blue-200" bodyClassName="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Foundry Contract v1 intake</div>
                  <div className="text-xs text-slate-600">
                    Structured intake for transcript, hypotheses, discovery questions and technical spec draft generation.
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => void generateContractAndSpecDraft()}
                  disabled={!resolvedProjectId || isProjectResolving || isContractSubmitting}
                >
                  {isContractSubmitting ? "Generating..." : "Generate contract + spec draft"}
                </Button>
              </div>

              {isContractLoading ? (
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  Loading latest Contract v1 draft...
                </div>
              ) : null}

              {contractError ? (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{contractError}</div>
              ) : null}

              {contractMessage ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                  {contractMessage}
                </div>
              ) : null}

              {contractValidationErrors.length > 0 ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {contractValidationErrors.map((entry) => (
                    <div key={`${entry.field}:${entry.code}`}>
                      {entry.field}: {entry.message}
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="grid gap-4 md:grid-cols-2">
                <label className="md:col-span-2 flex flex-col gap-1 text-sm text-slate-700">
                  Transcript evidence
                  <textarea
                    value={draft.transcriptText}
                    onChange={(event) => updateDraft("transcriptText", event.target.value)}
                    className="min-h-28 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    placeholder="Paste a meeting transcript or key exchange summary (minimum 80 characters)."
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  Transcript source
                  <input
                    value={draft.transcriptSource}
                    onChange={(event) => updateDraft("transcriptSource", event.target.value)}
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    placeholder="smartphone"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  Transcript language
                  <input
                    value={draft.transcriptLanguage}
                    onChange={(event) => updateDraft("transcriptLanguage", event.target.value)}
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    placeholder="fr"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  Hypotheses (one per line)
                  <textarea
                    value={draft.hypotheses}
                    onChange={(event) => updateDraft("hypotheses", event.target.value)}
                    className="min-h-20 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  Open questions (one per line)
                  <textarea
                    value={draft.openQuestions}
                    onChange={(event) => updateDraft("openQuestions", event.target.value)}
                    className="min-h-20 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  MVP scope (one per line)
                  <textarea
                    value={draft.mvpScope}
                    onChange={(event) => updateDraft("mvpScope", event.target.value)}
                    className="min-h-20 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  NFR (one per line)
                  <textarea
                    value={draft.nfr}
                    onChange={(event) => updateDraft("nfr", event.target.value)}
                    className="min-h-20 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  Risks (one per line)
                  <textarea
                    value={draft.risks}
                    onChange={(event) => updateDraft("risks", event.target.value)}
                    className="min-h-20 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm text-slate-700">
                  KPIs (one per line)
                  <textarea
                    value={draft.kpis}
                    onChange={(event) => updateDraft("kpis", event.target.value)}
                    className="min-h-20 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </label>
              </div>

              {contractDraft ? (
                <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                  <div className="grid gap-2 text-xs text-slate-700 md:grid-cols-2">
                    <div>Draft ID: {contractDraft.id}</div>
                    <div>Created: {new Date(contractDraft.createdAt).toLocaleString()}</div>
                    <div>Readiness: {contractDraft.assessment.isReadyForSpec ? "READY" : "BLOCKED"}</div>
                    <div>Confidence: {(contractDraft.assessment.confidenceScore * 100).toFixed(0)}%</div>
                  </div>
                  <div className="grid gap-2 md:grid-cols-2">
                    <div className="rounded border border-slate-200 bg-white p-2">
                      <div className="text-xs font-semibold text-slate-900">Assessment blocking reasons</div>
                      <div className="mt-1 text-xs text-slate-700">
                        {contractDraft.assessment.blockingReasons.length
                          ? contractDraft.assessment.blockingReasons.slice(0, 4).join(" | ")
                          : "None"}
                      </div>
                    </div>
                    <div className="rounded border border-slate-200 bg-white p-2">
                      <div className="text-xs font-semibold text-slate-900">Technical spec draft summary</div>
                      <div className="mt-1 text-xs text-slate-700">{contractDraft.specDraft.summary}</div>
                    </div>
                  </div>
                  <div className="rounded border border-slate-200 bg-white p-2">
                    <div className="text-xs font-semibold text-slate-900">Initial backlog preview</div>
                    <div className="mt-1 grid gap-1 text-xs text-slate-700">
                      {contractDraft.specDraft.initialBacklog.slice(0, 6).map((item) => (
                        <div key={item.id}>
                          {item.id} [{item.priority}] {item.title}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
            </Card>
          </div>
        ) : null}

        {activeStep.id === "intent" ? (
          <div className="grid gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Application name
              <input
                value={draft.appName}
                onChange={(event) => updateDraft("appName", event.target.value)}
                className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="OpsPilot Companion"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Application type
              <select
                value={draft.appType}
                onChange={(event) =>
                  updateDraft("appType", event.target.value as FoundryV2Draft["appType"])
                }
                className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="internal-dashboard">Internal dashboard</option>
                <option value="customer-portal">Customer portal</option>
                <option value="api-platform">API platform</option>
                <option value="data-pipeline">Data pipeline</option>
              </select>
            </label>
            <label className="md:col-span-2 flex flex-col gap-1 text-sm text-slate-700">
              Business goal
              <textarea
                value={draft.businessGoal}
                onChange={(event) => updateDraft("businessGoal", event.target.value)}
                className="min-h-24 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="Describe the expected business outcome and target users."
              />
            </label>
            <label className="md:col-span-2 flex flex-col gap-1 text-sm text-slate-700">
              Constraints
              <textarea
                value={draft.constraints}
                onChange={(event) => updateDraft("constraints", event.target.value)}
                className="min-h-20 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="Compliance, data residency, budget, deadlines, integrations."
              />
            </label>
          </div>
        ) : null}

        {activeStep.id === "architecture" ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <div className="text-sm font-semibold text-slate-900">AI recommendation for {draft.appType}</div>
              <div className="mt-1 text-xs text-slate-600">
                Confidence: {(recommendation.confidence * 100).toFixed(0)}% | Source:{" "}
                {architectureRecommendation ? "Foundry service" : "local fallback"}
              </div>
              <div className="mt-1 text-sm text-slate-700">{recommendation.rationale}</div>
              <div className="mt-2 grid gap-2 text-xs text-slate-700 md:grid-cols-3">
                <div>Frontend: {recommendation.frontend}</div>
                <div>Backend: {recommendation.backend}</div>
                <div>Database: {recommendation.database}</div>
                <div>Runtime: {recommendation.runtime}</div>
                <div>Deploy: {recommendation.deployment}</div>
                <div>Auth: {recommendation.auth}</div>
              </div>
              <div className="mt-3 grid gap-2 text-xs text-slate-700 md:grid-cols-4">
                <div className="rounded border border-slate-200 bg-white px-2 py-1">Cost: {recommendation.tradeoffs.cost}</div>
                <div className="rounded border border-slate-200 bg-white px-2 py-1">
                  Complexity: {recommendation.tradeoffs.complexity}
                </div>
                <div className="rounded border border-slate-200 bg-white px-2 py-1">
                  Lock-in: {recommendation.tradeoffs.lockIn}
                </div>
                <div className="rounded border border-slate-200 bg-white px-2 py-1">
                  Compliance: {recommendation.tradeoffs.compliance}
                </div>
              </div>
              <div className="mt-3 rounded border border-slate-200 bg-white p-2">
                <div className="text-xs font-semibold text-slate-800">Internal execution plan preview</div>
                {isExecutionPlanLoading ? (
                  <div className="mt-1 text-xs text-slate-600">Loading planner graph...</div>
                ) : executionPlanError ? (
                  <div className="mt-1 text-xs text-amber-700">{executionPlanError}</div>
                ) : executionPlanPreview ? (
                  <div className="mt-1 space-y-2">
                    <div className="text-xs text-slate-700">
                      {executionPlanPreview.stages.length} stages | budget {executionPlanPreview.budget.maxIterations} iterations /{" "}
                      {executionPlanPreview.budget.maxMinutes} min
                    </div>
                    <div className="grid gap-1 text-[11px] text-slate-700 md:grid-cols-2">
                      {executionPlanPreview.stages.slice(0, 6).map((stage) => (
                        <div key={stage.id} className="rounded border border-slate-200 bg-slate-50 px-2 py-1">
                          {stage.label} ({stage.id})
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mt-1 text-xs text-slate-600">No plan preview available.</div>
                )}
              </div>
              {isRecommendationLoading ? (
                <div className="mt-2 text-xs text-slate-600">Refreshing recommendation...</div>
              ) : null}
              {recommendationError ? (
                <div className="mt-2 text-xs text-amber-700">
                  {recommendationError}. Using local recommendation fallback.
                </div>
              ) : null}
              <div className="mt-3">
                <Button variant="secondary" size="sm" onClick={applyAiRecommendation}>
                  Apply AI recommendation
                </Button>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <label className="flex flex-col gap-1 text-sm text-slate-700">
                Runtime
                <select
                  value={draft.runtime}
                  onChange={(event) => updateDraft("runtime", event.target.value as FoundryV2Draft["runtime"])}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                >
                  {architecturePresets.runtime.map((runtime) => (
                    <option key={runtime} value={runtime}>
                      {RUNTIME_LABELS[runtime]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm text-slate-700">
                Frontend
                <select
                  value={draft.frontend}
                  onChange={(event) =>
                    updateDraft("frontend", event.target.value as FoundryV2Draft["frontend"])
                  }
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="nextjs">Next.js</option>
                  <option value="react-vite">React + Vite</option>
                  <option value="nuxt">Nuxt</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm text-slate-700">
                Backend
                <select
                  value={draft.backend}
                  onChange={(event) =>
                    updateDraft("backend", event.target.value as FoundryV2Draft["backend"])
                  }
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="nestjs">NestJS</option>
                  <option value="express">Express</option>
                  <option value="fastapi">FastAPI</option>
                  <option value="spring">Spring</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm text-slate-700">
                Database
                <select
                  value={draft.database}
                  onChange={(event) =>
                    updateDraft("database", event.target.value as FoundryV2Draft["database"])
                  }
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                >
                  {architecturePresets.database.map((database) => (
                    <option key={database} value={database}>
                      {DATABASE_LABELS[database]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm text-slate-700">
                Deployment
                <select
                  value={draft.deployment}
                  onChange={(event) =>
                    updateDraft("deployment", event.target.value as FoundryV2Draft["deployment"])
                  }
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                >
                  {architecturePresets.deployment.map((deployment) => (
                    <option key={deployment} value={deployment}>
                      {DEPLOYMENT_LABELS[deployment]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm text-slate-700">
                Authentication
                <select
                  value={draft.auth}
                  onChange={(event) => updateDraft("auth", event.target.value as FoundryV2Draft["auth"])}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="auth0">Auth0</option>
                  <option value="clerk">Clerk</option>
                  <option value="cognito">AWS Cognito</option>
                  <option value="sso">Enterprise SSO</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm text-slate-700 md:col-span-2 lg:col-span-3">
                Observability
                <select
                  value={draft.observability}
                  onChange={(event) =>
                    updateDraft("observability", event.target.value as FoundryV2Draft["observability"])
                  }
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="otel-sentry">OpenTelemetry + Sentry</option>
                  <option value="otel-datadog">OpenTelemetry + Datadog</option>
                  <option value="basic-logs">Basic logs only</option>
                </select>
              </label>
            </div>
          </div>
        ) : null}

        {activeStep.id === "environment" ? (
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2 rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="foundry-runtime-profile-panel">
              <div className="text-sm font-semibold text-slate-900">Runtime operating profile</div>
              <div className="mt-1 text-xs text-slate-600">
                This choice governs IAM, secrets, audit, observability, backup, retention, and rollback requirements before Jarvis handoff.
              </div>
              <div className="mt-3 grid gap-2 xl:grid-cols-5">
                {RUNTIME_PROFILE_OPTIONS.map((option) => {
                  const selected = draft.runtimeProfile === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => updateDraft("runtimeProfile", option.id)}
                      className={`rounded-xl border px-3 py-3 text-left transition ${
                        selected
                          ? "border-blue-400 bg-blue-50 shadow-[0_12px_30px_rgba(59,130,246,0.12)]"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      <div className="text-sm font-semibold text-slate-900">{option.label}</div>
                      <div className="mt-1 text-[11px] leading-5 text-slate-600">{option.summary}</div>
                      <div className="mt-3 text-[11px] font-medium text-slate-500">{option.stackHint}</div>
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50 px-3 py-3 text-xs text-slate-700">
                {
                  (RUNTIME_PROFILE_OPTIONS.find((option) => option.id === draft.runtimeProfile) ?? RUNTIME_PROFILE_OPTIONS[0])
                    .controlHeadline
                }
              </div>
            </div>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              GitHub integration
              <select
                value={draft.githubMode}
                onChange={(event) =>
                  updateDraft("githubMode", event.target.value as FoundryV2Draft["githubMode"])
                }
                className="rounded-md border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="local-only">No GitHub (local only)</option>
                <option value="existing-repo">Connect existing repository</option>
                <option value="create-repo">Create repository automatically</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Repository name
              <input
                value={draft.repositoryName}
                onChange={(event) => updateDraft("repositoryName", event.target.value)}
                className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                placeholder="longwalls-proof-app"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Compliance profile
              <select
                value={draft.complianceProfile}
                onChange={(event) =>
                  updateDraft(
                    "complianceProfile",
                    event.target.value as FoundryV2Draft["complianceProfile"],
                  )
                }
                className="rounded-md border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="standard">Standard</option>
                <option value="restricted">Restricted</option>
                <option value="highly-regulated">Highly regulated</option>
              </select>
            </label>
            <label className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={draft.autoPlanning}
                onChange={(event) => updateDraft("autoPlanning", event.target.checked)}
              />
              Enable internal auto-planning (requirements/workitems hidden from user flow)
            </label>
            <div className="md:col-span-2 rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="text-sm font-semibold text-slate-900">External integrations</div>
              <div className="mt-2 grid gap-2 md:grid-cols-2">
                {INTEGRATION_OPTIONS.map((option) => {
                  const selected = draft.integrationTargets.includes(option.id);
                  return (
                    <label
                      key={option.id}
                      className={`flex items-start gap-2 rounded border px-2 py-2 text-xs ${
                        selected ? "border-blue-300 bg-blue-50 text-blue-900" : "border-slate-200 bg-white text-slate-700"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleIntegrationTarget(option.id)}
                      />
                      <span>
                        <span className="block font-semibold">{option.label}</span>
                        <span className="block text-[11px] text-slate-600">Keys: {option.envHint}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
            <label className="md:col-span-2 flex flex-col gap-1 text-sm text-slate-700">
              Integration notes (optional)
              <textarea
                value={draft.integrationNotes}
                onChange={(event) => updateDraft("integrationNotes", event.target.value)}
                className="min-h-20 rounded-md border border-slate-300 px-3 py-2 text-sm"
                placeholder="Endpoints, provider account names, tenant mapping, webhook notes."
              />
            </label>
            <div className="md:col-span-2 rounded-md border border-blue-200 bg-blue-50 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-semibold text-slate-900">Suggested environment keys</div>
                <Button variant="secondary" size="sm" onClick={applySuggestedEnvKeys}>
                  Use suggested keys
                </Button>
              </div>
              <div className="mt-2 text-xs text-slate-700">
                Effective keys ({effectiveEnvKeys.length}): {effectiveEnvKeys.join(", ") || "none"}
              </div>
            </div>
            <label className="md:col-span-2 flex flex-col gap-1 text-sm text-slate-700">
              Additional environment keys (optional, one per line)
              <textarea
                value={draft.envKeys}
                onChange={(event) => updateDraft("envKeys", event.target.value)}
                className="min-h-24 rounded-md border border-slate-300 px-3 py-2 text-sm"
                placeholder={"ERP_API_BASE_URL\nERP_CLIENT_ID\nERP_CLIENT_SECRET"}
              />
            </label>
          </div>
        ) : null}

        {activeStep.id === "review" ? (
          <div className="space-y-4">
            <div className="grid gap-2">
              {readinessChecks.map((item) => (
                <div
                  key={item.id}
                  className={`rounded-md border px-3 py-2 text-sm ${
                    item.ok
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-amber-200 bg-amber-50 text-amber-700"
                  }`}
                >
                  {item.ok ? "PASS" : "MISSING"} - {item.label}
                </div>
              ))}
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="text-sm font-semibold text-slate-900">Handoff summary</div>
              <div className="mt-2 grid gap-2 text-sm text-slate-700 md:grid-cols-2">
                <div>Project: {resolvedProjectName || draft.projectId || "-"}</div>
                <div>Resolved project ID: {resolvedProjectId || "-"}</div>
                <div>App: {draft.appName || "-"}</div>
                <div>Type: {draft.appType}</div>
                <div>Frontend: {draft.frontend}</div>
                <div>Backend: {draft.backend}</div>
                <div>Database: {draft.database}</div>
                <div>Deploy: {draft.deployment}</div>
                <div>Auth: {draft.auth}</div>
                <div>Runtime profile: {draft.runtimeProfile}</div>
                <div>Compliance: {draft.complianceProfile}</div>
                <div>Contract draft: {contractDraft?.id ?? "-"}</div>
                <div>Contract readiness: {contractDraft?.assessment?.isReadyForSpec ? "ready" : "missing/blocking"}</div>
                <div>Environment keys: {effectiveEnvKeys.length}</div>
                <div>
                  Integrations: {draft.integrationTargets.length ? draft.integrationTargets.join(", ") : "none"}
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="foundry-build-ready-panel">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Build-ready + Forge capability</div>
                  <div className="mt-1 text-xs text-slate-600">
                    This panel is the deterministic handoff contract between Atlas outputs, Foundry artifacts, and Jarvis delivery.
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => resolvedProjectId && navigate(`/jarvis?projectId=${encodeURIComponent(resolvedProjectId)}`)}
                    disabled={!resolvedProjectId}
                  >
                    Open Jarvis delivery
                  </Button>
                </div>
              </div>

              {isBuildReadyLoading ? (
                <div className="mt-3 text-xs text-slate-600">Loading build-ready posture...</div>
              ) : null}
              {buildReadyError ? (
                <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {buildReadyError}
                </div>
              ) : null}

              <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-md border border-slate-200 bg-white p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Contract v1</div>
                  <div className="mt-2 text-sm font-semibold text-slate-900">
                    {contractDraft?.assessment?.isReadyForSpec ? "Build-ready" : "Needs work"}
                  </div>
                  <div className="mt-1 text-xs text-slate-600">
                    {contractDraft ? `Draft ${contractDraft.id}` : "No deterministic contract generated yet."}
                  </div>
                </div>
                <div className="rounded-md border border-slate-200 bg-white p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Forge handoff</div>
                  <div className="mt-2 text-sm font-semibold text-slate-900">
                    {latestHandoff ? latestHandoff.status : "Not handed off"}
                  </div>
                  <div className="mt-1 text-xs text-slate-600">
                    {latestHandoff ? `${latestHandoff.specSectionCount} spec sections • planner ${latestHandoff.planner.stageCount} stages` : "Waiting for locked handoff package."}
                  </div>
                </div>
                <div className="rounded-md border border-slate-200 bg-white p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Pilot gate</div>
                  <div className="mt-2 text-sm font-semibold text-slate-900">
                    {pilotGate ? `${pilotGate.status} (${pilotGate.score}/100)` : "Pending"}
                  </div>
                  <div className="mt-1 text-xs text-slate-600">
                    {pilotGate
                      ? `${pilotGate.summary.greenRuns}/${pilotGate.summary.requiredGreenRuns} green runs • rollback ${pilotGate.summary.rollbackReady ? "ready" : "missing"}`
                      : "Pilot gate will appear once the project has crossed the first Jarvis proof run."}
                  </div>
                </div>
                <div className="rounded-md border border-slate-200 bg-white p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Next move</div>
                  <div className="mt-2 text-sm text-slate-700">
                    {latestHandoff
                      ? "Open Jarvis, review approvals and evidence, then launch or replay the pilot run."
                      : contractDraft?.assessment?.isReadyForSpec
                        ? "Lock the handoff package and push the build-ready contract into Jarvis."
                        : "Close contract gaps until Foundry marks the project build-ready."}
                  </div>
                </div>
              </div>
            </div>

            {handoffError ? (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {handoffError}
              </div>
            ) : null}

            {handoffMessage ? (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                {handoffMessage}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="foundry-v2-screen__footer flex flex-wrap items-center justify-between border-t border-slate-200 pt-4">
          <Button variant="secondary" size="sm" onClick={goBack} disabled={activeStepIndex === 0}>
            Back
          </Button>
          <div className="foundry-v2-screen__footer-actions flex items-center gap-2">
            {activeStep.id === "review" ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => void handoffToJarvisV2()}
                disabled={!isReadyForHandoff || isHandoffSubmitting}
              >
                {isHandoffSubmitting ? "Locking..." : "Lock blueprint and handoff to Jarvis"}
              </Button>
            ) : (
              <Button variant="primary" size="sm" onClick={goNext}>
                Next
              </Button>
            )}
            {activeStep.id === "context" ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setContextRefreshKey((current) => current + 1)}
                disabled={!resolvedProjectId || isProjectResolving}
              >
                Refresh context modules
              </Button>
            ) : null}
          </div>
        </div>
      </Card>
      </div>
    </FoundryV2AccessGuard>
  );
}
